"use client";

// Module-level flag for cross-component synchronous communication.
// When a manual call starts while multi-line is active, this flag is set SYNCHRONOUSLY
// before any React render cycle. dialNextLeads checks this flag and aborts immediately.
// No React state, no refs, no effects — pure JS variable, zero race conditions.
// Use sessionStorage to survive Next.js page navigations (App Router soft/hard nav)
const MANUAL_PAUSE_KEY = '__multiline_manual_pause';
const MANUAL_AUTO_STOPPED_KEY = '__multiline_auto_stopped';

export function requestManualCallPause() {
  try { sessionStorage.setItem(MANUAL_PAUSE_KEY, '1'); sessionStorage.setItem(MANUAL_AUTO_STOPPED_KEY, '1'); } catch {}
}
export function isManualCallPauseRequested() {
  try { return sessionStorage.getItem(MANUAL_PAUSE_KEY) === '1'; } catch { return false; }
}
export function clearManualCallPauseRequest() {
  try { sessionStorage.removeItem(MANUAL_PAUSE_KEY); } catch {}
}
export function wasManualCallAutoStopped() {
  try { return sessionStorage.getItem(MANUAL_AUTO_STOPPED_KEY) === '1'; } catch { return false; }
}
export function clearManualCallAutoStopped() {
  try { sessionStorage.removeItem(MANUAL_AUTO_STOPPED_KEY); } catch {}
}

/**
 * Module-level dedup for `/communication/calls/:sid/cancel` POSTs.
 *
 * Why this exists: during Hold & Answer in a multi-line session, we saw
 * 6 `/cancel` POSTs fire for the same callSid in <1 second (logs confirmed).
 * Multiple code paths try to cancel the same call independently:
 *   - TwilioDeviceContext.disconnect() when the user hangs up
 *   - MultiLineDialerSession polling loop when a line goes terminal
 *   - handleSkipAllRinging cancel-losers path
 *   - Race-winner cancel-losers path
 *   - Agent-SDK error handlers
 *
 * Each path is legitimate in isolation but the aggregate cascade can:
 *   - Hit Twilio's rate limits
 *   - Return stale errors that confuse subsequent flows
 *   - Kill calls that just started (new call's SID briefly overlaps old)
 *
 * Gated here so EVERY /cancel POST goes through one dedup check. TTL is
 * 30s per callSid (covers the longest possible legitimate retry window;
 * after 30s the call is definitely terminal anyway).
 */
const CANCEL_DEDUP_TTL_MS = 30_000;
const cancelDedupMap: Map<string, number> = new Map();

export function shouldCancelCall(callSid: string): boolean {
  if (!callSid) return false;
  const now = Date.now();
  // Garbage-collect stale entries to keep the map bounded.
  if (cancelDedupMap.size > 200) {
    for (const [sid, ts] of cancelDedupMap) {
      if (now - ts > CANCEL_DEDUP_TTL_MS) cancelDedupMap.delete(sid);
    }
  }
  const last = cancelDedupMap.get(callSid);
  if (last && now - last < CANCEL_DEDUP_TTL_MS) {
    return false; // Already canceled recently — skip this call.
  }
  cancelDedupMap.set(callSid, now);
  return true;
}

export function clearCancelDedup(callSid?: string) {
  if (callSid) cancelDedupMap.delete(callSid);
  else cancelDedupMap.clear();
}

import React, { createContext, useContext, useEffect, useState, useRef, useCallback, useMemo } from "react";
import { Device, Call } from "@twilio/voice-sdk";
import { createDemoDevice } from "./demo-voice-device";
import { createTelnyxDevice, TelnyxDevice } from "./telnyx-device-adapter";
import { isDemoMode } from "../utils/demoMode";
import api from "@/app/_module/services/api";
import { getApiUrl } from "@/app/_module/services/config";
import { endpoint } from "@/app/_module/services/endpoints";
import { useToast } from "@/app/_module/hooks/use-toast";
import { useUserContext } from "@/app/_module/store/contexts/userContext/userContexts";
import { accountTypeHasTelephony } from "@/app/_module/components/TelephonyEnabled";
import { useNotificationSound } from "@/app/_module/hooks/use-notification-sound";
import communicationService from "@/app/_module/services/communicationService";
import {
  callService,
  ConferenceParticipant,
  AddParticipantRequest,
  ControlParticipantRequest
} from '../services/callService';
import { suppressTwilioLogs, restoreConsoleLogs } from "@/app/_module/utils/suppressTwilioLogs";
import { startWhisperReceiver, type WhisperReceiverHandle } from "@/app/_module/utils/whisper-receiver";
import { softenCallAudio } from "@/app/_module/utils/loud-call-limiter";
import { getCallSid, getCallParam as getCallParamHelper, onCallDisconnect, hangupCall, sendHangupCancel } from "./voice-call-helpers";
import { getSupabaseClient } from "@/app/_module/services/supabaseClient";
import { formatDurationShort } from "@/app/_module/utils/formatters";

// Detect Safari (desktop & mobile) — Safari requires a user gesture for setSinkId(),
// which causes "NotAllowedError: A user gesture is required" when Twilio SDK tries
// to route audio to a specific output device internally.
// ⚠️ Includes Capacitor's WKWebView: its UA has NO "Safari" token, so the
// original regex evaluated false inside the native app and every WebKit
// workaround (setSinkId guards, unhandled-rejection suppressor) was skipped —
// iOS ran the desktop-Chrome code paths. Same engine, same quirks.
const isSafariBrowser = typeof navigator !== 'undefined' && (
  /^((?!chrome|android).)*safari/i.test(navigator.userAgent) ||
  /iPad|iPhone|iPod/.test(navigator.userAgent) ||
  (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1)
);

// This tab's phone slot (0-7), sent with the voice token. Telnyx allows one live login per
// credential (a second tab's login logs the first out), so the backend gives each browser
// (x-device-id) + slot its own; Twilio ignores it. The slot is the first free
// `ic-phone-slot-<n>` Web Lock, held while the tab lives: a reload usually gets its slot
// back, so a browser stays at 8 credentials or fewer. No Web Locks: one slot per tab session.
const PHONE_SLOTS = 8;
let phoneSlotClaim: Promise<number> | null = null;

function sessionPhoneSlot(): number {
  try {
    const saved = sessionStorage.getItem('ic-phone-slot');
    if (saved && /^[0-7]$/.test(saved)) return Number(saved);
    const slot = Math.floor(Math.random() * PHONE_SLOTS);
    sessionStorage.setItem('ic-phone-slot', String(slot));
    return slot;
  } catch {
    return 0;
  }
}

async function claimPhoneSlot(): Promise<number> {
  const locks = typeof navigator === 'undefined' ? undefined : navigator.locks;
  if (!locks?.request) return sessionPhoneSlot();
  for (let n = 0; n < PHONE_SLOTS; n++) {
    const held = await new Promise<boolean>((resolve, reject) => {
      locks
        .request(`ic-phone-slot-${n}`, { ifAvailable: true }, (lock) => {
          resolve(!!lock);
          return lock ? new Promise<void>(() => {}) : undefined; // kept until the tab goes
        })
        .catch(reject);
    });
    if (held) return n;
  }
  console.warn('[TwilioDevice] all 8 phone slots are in use in this browser; this tab shares slot 7');
  return PHONE_SLOTS - 1;
}

/** Never holds up the token: a lock manager that fails or takes over 2 s means the session slot. */
export function phoneSlot(): Promise<number> {
  return (phoneSlotClaim ??= Promise.race([
    claimPhoneSlot().catch(() => -1),
    new Promise<number>((resolve) => setTimeout(resolve, 2000, -1)),
  ]).then((slot) => (slot < 0 ? sessionPhoneSlot() : slot)));
}

// localStorage key for persisting active call session across page refresh
const ACTIVE_CALL_SESSION_KEY = 'active-call-session';

// Interface for persisted call session data
interface PersistedCallSession {
  callSid: string;
  conferenceName: string;
  leadId?: string;
  leadName?: string;
  phoneNumber: string;
  startTime: number;
  mode: 'power_dialer' | 'manual' | 'incoming';
  source?: string;
}

interface BridgedCallState {
  callSid: string;
  status: string;
  leadPhone: string;
  leadName?: string;
  createdAt: Date;
  isBridgedCall?: boolean; // True for phone-forwarded calls
  phoneCallSid?: string; // The phone leg call SID for forwarded calls
  conferenceName?: string; // Conference name for forwarded calls
}

interface CallMetadata {
  leadName?: string;
  leadId?: string;
  phoneNumber?: string;
  leadEmail?: string;
  source?: string;
  parentCallSid?: string; // Original caller's CallSid for incoming conference calls
  outboundCallerId?: string; // The phone number the system is dialing FROM
}

interface AudioDevice {
  deviceId: string;
  label: string;
  kind: 'audioinput' | 'audiooutput';
}

// Format a Twilio phone number (+13133743390) into a human-readable form
// for hangup attribution toasts: "(313) 374-3390".
function formatPhoneForDisplay(phone: string): string {
  const digits = phone.replace(/\D/g, '');
  const us = digits.length === 11 && digits.startsWith('1') ? digits.slice(1) : digits;
  if (us.length !== 10) return phone;
  return `(${us.slice(0, 3)}) ${us.slice(3, 6)}-${us.slice(6)}`;
}

// Concise duration formatter for hangup-attribution toasts. Imported from the
// shared util so the multi-line dialer renders identical strings.
// Re-exported as formatDurationLabel for legacy local naming.
const formatDurationLabel = formatDurationShort;

// Color-coded left border for hangup-attribution toasts so agents can tell at
// a glance who ended the call. Colors are intentionally subtle (left stripe
// only, no background tint) to stay consistent with the app's white toast
// design while still giving an instant visual signal.
const HANGUP_TOAST_CLASS = {
  // Green stripe — agent completed a call (positive, normal end)
  agentEndedConnected: 'border-l-4 border-l-green-500',
  // Neutral — agent cancelled their own action (cancelled while ringing, declined)
  agentCancelled: 'border-l-4 border-l-gray-300',
  // Red stripe — lead disconnected mid-call or dropped instantly (no fault of agent)
  leadHungUp: 'border-l-4 border-l-red-500',
  // Amber stripe — actionable miss (lead didn't pick up or hung up before agent answered)
  needsCallback: 'border-l-4 border-l-amber-500',
};

interface TwilioDeviceContextValue {
  device: Device | null;
  activeCall: Call | null;
  activeCallSid: string | null;
  callMetadata: CallMetadata | null;
  isDeviceReady: boolean;
  isDeviceRegistering: boolean;
  deviceError: string | null;
  forceShowWidget: boolean; // When true, show ActiveCallWidget even on dialer page
  connect: (params: { to: string; from?: string; displayFrom?: string; leadId?: string; metadata?: Record<string, any>; forceShowWidget?: boolean }) => Promise<Call | null>;
  disconnect: (options?: { force?: boolean; userInitiated?: boolean; skipServerCancel?: boolean }) => void;
  mute: () => void;
  unmute: () => void;
  hold: (callSid?: string) => void;
  unhold: (callSid?: string) => void;
  startRecording: () => void;
  stopRecording: () => void;
  sendDigits: (digits: string) => boolean;
  isMuted: boolean;
  isOnHold: boolean;
  setIsOnHold: (value: boolean) => void;
  isRecording: boolean;
  callStatus: string;
  callDuration: number;
  remoteAudioRef: React.RefObject<HTMLAudioElement | null>;
  forwardCallsToPhone: boolean;
  setForwardCallsToPhone: (enabled: boolean) => void;
  initializeDevice: () => Promise<void>;
  /** Supervisor Barge auto-accept lifecycle. `arm` opens a one-shot window;
   *  `setExpected` records the exact leg CallSid to auto-answer (from the
   *  /coach/barge/join response); `disarm` cancels the window on failure;
   *  `disconnect` hangs up the accepted coach leg (removes them from the call). */
  armCoachBargeAutoAccept: () => void;
  setCoachBargeExpectedSid: (sid: string) => void;
  disarmCoachBargeAutoAccept: () => void;
  disconnectCoachBarge: () => void;
  createBridgedCall: (params: { to: string; from?: string; leadId?: string; leadName?: string }) => Promise<{ callSid: string; status: string; message: string }>;
  bridgedCalls: Map<string, BridgedCallState>;
  // Audio level monitoring - powers mic level meter in AudioDeviceSelector
  microphoneAudioLevel: number; // 0-100
  isHardwareMuted: boolean;
  startAudioMonitoring: () => void; // Start mic level monitoring (call when audio settings open)
  stopAudioMonitoring: () => void; // Stop mic level monitoring (call when audio settings close)
  // Audio device selection
  audioInputDevices: AudioDevice[];
  audioOutputDevices: AudioDevice[];
  selectedInputDeviceId: string | null;
  selectedOutputDeviceId: string | null;
  setAudioInputDevice: (deviceId: string) => Promise<void>;
  setAudioOutputDevice: (deviceId: string) => Promise<void>;
  refreshAudioDevices: () => Promise<void>;
  // Audio gain/volume controls
  microphoneGain: number; // 0-200, 100 = normal, >100 = amplified
  speakerVolume: number; // 0-200, 100 = normal
  setMicrophoneGain: (gain: number) => void;
  setSpeakerVolume: (volume: number) => void;
  // 3-Way Calling
  conferenceParticipants: ConferenceParticipant[];
  isAddingParticipant: boolean;
  addParticipantToCall: (phoneNumber: string, name?: string, role?: 'agent' | 'customer' | 'consultant') => Promise<boolean>;
  removeParticipantFromCall: (participantSid: string) => Promise<boolean>;
  removeParticipant: (participantSid: string) => Promise<boolean>; // Alias for removeParticipantFromCall
  muteParticipant: (participantSid: string, mute: boolean) => Promise<boolean>;
  holdParticipant: (participantSid: string, hold: boolean) => Promise<boolean>;
  refreshConferenceParticipants: () => Promise<void>;
  setActiveConference: (conferenceName: string | null) => void;
  currentConferenceName: string | null;
  // Ringback control - for multi-line dialer to control ringing audio
  stopRingback: () => void;
  playRingback: () => void;
  // Dialer session mode tracking (prevents incoming call conflicts)
  isInMultiLineMode: boolean;
  setIsInMultiLineMode: (value: boolean) => void;
  isInPowerDialerMode: boolean;
  setIsInPowerDialerMode: (value: boolean) => void;
  // Event handlers called by IncomingCallContext (Supabase event delegation)
  handleCustomerDisconnected: (payload: { parentCallSid: string; customerCallSid?: string; callStatus?: string }) => void;
  handleParticipantConnected: (payload: { participantSid: string; conferenceSid?: string; conferenceName?: string }) => void;
  handleParticipantLeft: (payload: { participantSid: string; conferenceSid?: string; conferenceName?: string }) => void;
  handleCallStatusUpdated: (payload: { callSid: string; status: string; duration?: number; isBridgedCall?: boolean }) => void;
  handlePhoneAnswered: (payload: { callSid: string; phoneCallSid?: string; conferenceName?: string; status?: string }) => void;
  handleCustomerAnswered: (payload: { parentCallSid: string; callerId?: string }) => void;
  // Partial update of call metadata (e.g., to set actual outbound caller ID from backend)
  updateCallMetadata: (updates: Partial<CallMetadata>) => void;
  // Call session recovery (page refresh)
  isRecoveringCall: boolean;
  recoveryError: string | null;
  // Device reconnect (iPad/iOS connection drops)
  isReconnecting: boolean;
  reconnectDevice: () => Promise<void>;
  // Wake Lock support detection — UI can warn users to keep screen on if unsupported
  wakeLockSupported: boolean;
  // Last dialed — for redial from manual keypad
  lastDialedNumber: string;
  lastDialedLeadName: string;
  // Tracks whether the current active call is manual, power_dialer, or multiline
  // Used by widget to show manual call UI over paused dialer session
  activeCallSource: 'manual' | 'power_dialer' | 'multiline' | null;
  // Provider-neutral active-call helpers — let consumers read the active call's
  // SID/params, subscribe to its disconnect, and hang it up WITHOUT touching the
  // raw SDK Call surface (Twilio `.parameters`/`.on`/`.disconnect` vs Telnyx
  // `.id`/state/`.hangup`). Both device contexts implement these identically.
  getActiveCallSid: () => string | null;
  getCallParam: (key: 'To' | 'From' | 'LeadId' | 'CallSid') => string | undefined;
  onActiveCallDisconnect: (cb: () => void) => () => void;
  hangupActiveCall: () => void;
  // LIVE TRANSFER (owner side)
  incomingTransferOffer: {
    agentName: string; leadName?: string; leadId?: string;
    customerNumber?: string; callSid: string; conferenceSid: string; participantCallSid: string;
  } | null;
  acceptTransferOffer: () => Promise<boolean>;
  declineTransferOffer: () => Promise<void>;
}

export const TwilioDeviceContext = createContext<TwilioDeviceContextValue | undefined>(undefined);

interface TwilioDeviceProviderProps {
  children: React.ReactNode;
  userId?: string;
}

export const TwilioDeviceProvider: React.FC<TwilioDeviceProviderProps> = ({ children, userId }) => {
  // A plan without a phone (Lite, or a trial of Lite) never gets a Twilio device:
  // the server refuses its voice token, and retrying into that 403 only produced a
  // "Calling unavailable" toast on every page. Kept in a ref so the start-up and
  // recovery paths (which run from `[]` effects) always see the latest answer;
  // unknown (profile not loaded yet) counts as "has a phone" so paid plans are
  // never delayed.
  const { user: planUser } = useUserContext();
  const planAccountType = (planUser as any)?.user?.accountType as string | undefined;
  // A free dialer account borrows the phone while its unlimited-dialer subscription is live.
  const planHasPhone =
    !planAccountType ||
    accountTypeHasTelephony(planAccountType, (planUser as any)?.user?.trialAccountTypeUsed) ||
    (planAccountType === "dialer" && (planUser as any)?.user?.unlimitedDialerActive === true);
  const planHasPhoneRef = useRef(true);
  planHasPhoneRef.current = planHasPhone;
  const [device, setDevice] = useState<Device | null>(null);
  const [activeCall, setActiveCall] = useState<Call | null>(null);
  const [activeCallSid, setActiveCallSid] = useState<string | null>(null);
  const [callMetadata, setCallMetadata] = useState<CallMetadata | null>(null);
  const [isDeviceReady, setIsDeviceReady] = useState(false);
  const [isDeviceRegistering, setIsDeviceRegistering] = useState(false);
  const [deviceError, setDeviceError] = useState<string | null>(null);
  const [isMuted, setIsMuted] = useState(false);
  const [isOnHold, setIsOnHold] = useState(false);
  const [isRecording, setIsRecording] = useState(false);
  const [callStatus, setCallStatus] = useState<string>("");
  const [callDuration, setCallDuration] = useState(0);
  const [forwardCallsToPhone, setForwardCallsToPhone] = useState(false);
  const [bridgedCalls, setBridgedCalls] = useState<Map<string, BridgedCallState>>(new Map());
  const [microphoneAudioLevel, setMicrophoneAudioLevel] = useState(0);
  const [isHardwareMuted, setIsHardwareMuted] = useState(false);
  const [activeCallSource, setActiveCallSource] = useState<'manual' | 'power_dialer' | 'multiline' | null>(null);
  const [forceShowWidget, setForceShowWidget] = useState(false);
  const [lastDialedNumber, setLastDialedNumberState] = useState(() => {
    if (typeof window !== 'undefined') {
      return localStorage.getItem('lastDialedNumber') || '';
    }
    return '';
  });
  const [lastDialedLeadName, setLastDialedLeadNameState] = useState(() => {
    if (typeof window !== 'undefined') {
      return localStorage.getItem('lastDialedLeadName') || '';
    }
    return '';
  });
  const setLastDialed = useCallback((num: string, leadName?: string) => {
    setLastDialedNumberState(num);
    setLastDialedLeadNameState(leadName || '');
    if (typeof window !== 'undefined') {
      localStorage.setItem('lastDialedNumber', num);
      localStorage.setItem('lastDialedLeadName', leadName || '');
    }
  }, []);
  const [audioInputDevices, setAudioInputDevices] = useState<AudioDevice[]>([]);
  const [audioOutputDevices, setAudioOutputDevices] = useState<AudioDevice[]>([]);
  const [selectedInputDeviceId, setSelectedInputDeviceId] = useState<string | null>(null);
  const [selectedOutputDeviceId, setSelectedOutputDeviceId] = useState<string | null>(null);
  // Audio gain/volume controls - 100 = normal (1.0x), 0 = muted, 200 = 2x amplified
  const [microphoneGain, setMicrophoneGainState] = useState(100);
  const [speakerVolume, setSpeakerVolumeState] = useState(100);
  const micGainNodeRef = useRef<GainNode | null>(null);
  // Web Audio API for speaker volume amplification (supports >100%)
  const speakerGainNodeRef = useRef<GainNode | null>(null);
  const speakerSourceNodeRef = useRef<MediaStreamAudioSourceNode | null>(null);
  // Reference to Twilio's remote audio element for volume control
  const twilioRemoteAudioRef = useRef<HTMLAudioElement | null>(null);
  // 3-Way Calling State
  const [conferenceParticipants, setConferenceParticipants] = useState<ConferenceParticipant[]>([]);
  const [isAddingParticipant, setIsAddingParticipant] = useState(false);
  const [currentConferenceName, setCurrentConferenceName] = useState<string | null>(null);
  // Dialer session state - tracks active dialer modes to prevent incoming call conflicts
  const [isInMultiLineMode, setIsInMultiLineMode] = useState(false);
  const [isInPowerDialerMode, setIsInPowerDialerMode] = useState(false);
  // Call session recovery state (for page refresh)
  const [isRecoveringCall, setIsRecoveringCall] = useState(false);
  const [recoveryError, setRecoveryError] = useState<string | null>(null);
  const hasAttemptedRecoveryRef = useRef(false); // Prevent multiple recovery attempts
  // Device reconnect state (for iPad/iOS connection drops)
  const [isReconnecting, setIsReconnecting] = useState(false);
  const reconnectAttemptRef = useRef(0);
  // Did we actually TELL the user the dialer was down? A silent blip that
  // recovers on the first retry must not pop a success toast — on a flaky
  // link (and on the iOS simulator, where WebRTC drops constantly) that
  // register→drop→register loop spammed "Dialer Reconnected" every cycle.
  const announcedDisconnectRef = useRef(false);
  const reconnectTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const MAX_RECONNECT_ATTEMPTS = 10;
  const lastTokenRefreshRef = useRef<number>(Date.now()); // Track last token refresh for proactive Safari refresh
  const remoteAudioRef = useRef<HTMLAudioElement>(null);
  const ringbackAudioRef = useRef<HTMLAudioElement | null>(null);
  const ringbackTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const durationIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const tokenRefreshTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  // Separate audio contexts to prevent conflicts:
  // - micAudioContextRef: for microphone level monitoring (can be closed on call end)
  // - speakerAudioContextRef: for speaker volume control (persists during call)
  const micAudioContextRef = useRef<AudioContext | null>(null);
  const speakerAudioContextRef = useRef<AudioContext | null>(null);
  const analyserRef = useRef<AnalyserNode | null>(null);
  const audioMonitorTimeoutRef = useRef<NodeJS.Timeout | null>(null); // Tracks the 3s delay before audio monitoring starts
  const audioLevelIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const lowAudioCountRef = useRef(0); // Track consecutive low audio readings
  const supabaseChannelRef = useRef<any>(null);
  const activeCallSidRef = useRef<string | null>(null); // Ref to track activeCallSid for Supabase callback
  const deviceRef = useRef<Device | null>(null); // Ref to avoid stale device in setTimeout callbacks

  // ── STARTUP ERROR NOISE ───────────────────────────────────────────────────
  //
  // /communication/voice/token is MFA-gated, so the first initializeDevice() during
  // the login window routinely 403s. The recovery effect at the bottom of this file
  // retries and the device comes up a few seconds later — but that failed first
  // attempt fired TWO destructive toasts ("Token Fetch Failed", then "Initialization
  // Failed" once fetchAccessToken's null return got rethrown) at a user whose dialer
  // then worked fine. Reported on prod 2026-08-22: "sometimes i see this but
  // everything loads good and all".
  //
  // These latches scope the toasts to failures that actually stick:
  //   • deviceEverUpRef — once the device has registered, every later failure is real
  //     (mid-session token refresh, network drop) and must still be reported loudly.
  //   • startupRecoveryDoneRef — set when the recovery effect gives up, so a genuinely
  //     broken startup surfaces instead of failing silently.
  // Suppressing only inside that window is the point: this hides the expected 403,
  // not real breakage.
  const deviceEverUpRef = useRef(false);
  const startupRecoveryDoneRef = useRef(false);
  const inStartupRecoveryWindow = useCallback(
    () => !deviceEverUpRef.current && !startupRecoveryDoneRef.current,
    []
  );
  const activeCallRef = useRef<Call | null>(null); // Ref to track activeCall for Supabase callback
  const parentCallSidRef = useRef<string | null>(null); // Ref to track parent call SID for incoming calls
  const wakeLockRef = useRef<WakeLockSentinel | null>(null); // Wake Lock to prevent screen sleep during calls (iOS fix)
  const isInMultiLineModeRef = useRef(false); // Ref to track multi-line mode for device.on("incoming") handler
  // Supervisor-barge one-shot auto-accept. We ONLY auto-answer the specific
  // Twilio leg whose CallSid matches the participant the backend just created
  // (expectedSid) — never a random incoming call. `expectedSid` is set by the
  // caller once /coach/barge/join responds; the incoming handler waits briefly
  // for it. `coachBargeCallRef` holds the accepted leg so we can hang it up.
  const coachBargeRef = useRef<{ expiry: number; expectedSid: string | null } | null>(null);
  const coachBargeCallRef = useRef<Call | null>(null);
  // LIVE TRANSFER (2026-07-23) — owner side. Armed by the transfer_offer
  // realtime broadcast (relayed as a window event); the matching device
  // incoming leg is stashed for a MANUAL accept/decline (unlike coach-barge's
  // auto-accept). Meta drives the "Transfer from {agent} — {lead}" card.
  const transferOfferRef = useRef<{
    expiry: number; participantCallSid: string; callSid: string;
    agentName: string; leadName?: string; leadId?: string;
    customerNumber?: string; conferenceSid: string;
    // Present when the owner's cell is ALSO ringing (call-forwarding) — the
    // CRM accept cancels it via resolve cancelLegSid.
    phoneParticipantCallSid?: string;
  } | null>(null);
  const transferCallRef = useRef<Call | null>(null);
  const [incomingTransferOffer, setIncomingTransferOffer] = useState<{
    agentName: string; leadName?: string; leadId?: string;
    customerNumber?: string; callSid: string; conferenceSid: string; participantCallSid: string;
    phoneParticipantCallSid?: string;
    isBack?: boolean;
    dropUserId?: string;
  } | null>(null);
  useEffect(() => {
    const onOffer = (e: any) => {
      const d = e?.detail || {};
      if (!d.participantCallSid) return;
      // Two-phase arming (2026-07-23 race fix): a PRE-ARM broadcast lands
      // BEFORE the leg is dialed (participantCallSid 'pending'), then a second
      // broadcast fills the real sid. Merge rather than replace so the
      // incoming handler's wait-loop sees the sid the moment it arrives.
      const cur = transferOfferRef.current;
      if (cur && cur.callSid === d.callSid) {
        transferOfferRef.current = { ...cur, ...d, expiry: Date.now() + 40000 };
      } else {
        transferOfferRef.current = { expiry: Date.now() + 40000, ...d };
      }
    };
    window.addEventListener('transfer:offer', onOffer);
    return () => window.removeEventListener('transfer:offer', onOffer);
  }, []);

  // LIVE TRANSFER (2026-07-23): CENTRAL agent-side drop. Whichever panel
  // initiated the transfer (manual widget, power compact, multi-line inline),
  // an 'accepted' status means the owner is live in the conference — drop OUR
  // device leg only (skipServerCancel: the customer must keep talking; agent
  // legs never end conferences).
  // STALE-CLOSURE FIX (2026-07-23, "agent still shows connected"): this effect
  // registers ONCE — a direct `disconnect` reference captured the mount-time
  // closure (activeCall null) and silently no-opped. Keep the LATEST
  // disconnect in a ref, refreshed every render.
  const disconnectFnRef = useRef<typeof disconnect | null>(null);
  useEffect(() => {
    const onStatus = (e: any) => {
      const d = e?.detail || {};
      if (d.status !== 'accepted') return;
      if (!activeCallRef.current) return; // nothing to drop (already ended)
      // SESSION PAUSE (2026-07-23, user request): transferring out of a dialer
      // session PAUSES it — same emergency-pause the incoming-call answer flow
      // uses — instead of auto-advancing. Agent resumes when ready.
      try {
        if (isInMultiLineModeRef.current) {
          window.dispatchEvent(new CustomEvent('multiline:emergency-pause', { detail: { reason: 'transfer' } }));
        } else if (isInPowerDialerModeRef.current) {
          window.dispatchEvent(new CustomEvent('powerdialer:emergency-pause', { detail: { reason: 'transfer' } }));
        }
      } catch { /* pause is best-effort */ }
      disconnectFnRef.current?.({ userInitiated: true, skipServerCancel: true });
    };
    window.addEventListener('transfer:status', onStatus);
    return () => window.removeEventListener('transfer:status', onStatus);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const armCoachBargeAutoAccept = useCallback(() => {
    coachBargeRef.current = { expiry: Date.now() + 15000, expectedSid: null };
  }, []);
  const setCoachBargeExpectedSid = useCallback((sid: string) => {
    if (coachBargeRef.current) coachBargeRef.current.expectedSid = sid;
  }, []);
  const disarmCoachBargeAutoAccept = useCallback(() => {
    coachBargeRef.current = null;
  }, []);
  const disconnectCoachBarge = useCallback(() => {
    try { coachBargeCallRef.current?.disconnect(); } catch { /* already gone */ }
    coachBargeCallRef.current = null;
    coachBargeRef.current = null;
  }, []);
  // Hangup attribution: set to 'user' inside disconnect() so the call.on("disconnect")
  // handler can tell whether the agent ended the call vs the remote party. Reset to
  // null after each toast so the next disconnect re-evaluates.
  const hangupAttributionRef = useRef<'user' | null>(null);
  // Mirror of callDuration state for the disconnect handler — reading state directly
  // would give a stale closure since handlers are registered when the call starts.
  const callDurationRef = useRef(0);
  // Mirror of callMetadata for the disconnect handler — same stale-closure reason.
  const callMetadataRef = useRef<CallMetadata | null>(null);
  const { toast} = useToast();
  // Centralized pickup chime for manual + power dialers. Multi-line has its
  // own earlier trigger (on polling detection of "connecting") so it skips
  // the window event dispatch in handleCustomerAnswered.
  const { playSound: playPickupChime } = useNotificationSound({ volume: 0.45 });

  // Keep device ref in sync with state for use in setTimeout callbacks (avoids stale closure)
  useEffect(() => {
    deviceRef.current = device;
  }, [device]);

  // Whisper sidecar — agent's browser receives supervisor whisper audio via
  // Socket.IO and plays it through Web Audio, mixed with the live call. The
  // lead never hears it because the audio never enters Twilio's media path.
  // See call-stream.gateway.ts:handleMicAudio for the routing decision and
  // whisper-receiver.ts for the playback details.
  //
  // We mount this whenever the dialer is open (TwilioDeviceProvider is
  // wrapped at the authenticated app level), regardless of whether a call
  // is active — registration is idempotent on the backend, and forwarding
  // simply no-ops when no manager is whispering.
  useEffect(() => {
    if (!userId) return;
    let socket: any = null;
    let receiver: WhisperReceiverHandle | null = null;
    let cancelled = false;

    (async () => {
      try {
        const { io } = await import("socket.io-client");
        if (cancelled) return;
        // Use the Supabase session helper (same as team-chat) instead of
        // localStorage.getItem("sb-access-token"). The generic key is empty;
        // Supabase stores under a project-ref-prefixed key.
        const { getSupabaseSession } = await import("@/app/_module/services/auth");
        const session = await getSupabaseSession();
        if (cancelled) return;
        const token = session?.access_token || "";
        const backendUrl = process.env.NEXT_PUBLIC_API_BASE_URL || "http://localhost:8080";

        socket = io(`${backendUrl}/call-stream`, {
          auth: { token },
          transports: ["websocket"],
          reconnection: true,
          reconnectionAttempts: 10,
          reconnectionDelay: 1000,
          reconnectionDelayMax: 8000,
        });

        // Race fix: same as the manager-side LiveCalls path. The 'connect'
        // event fires when the WS handshake completes, but the gateway's
        // handleConnection runs `await supabase.auth.getUser` AFTER that —
        // so socketUserIds isn't populated yet. Any agent-register emitted
        // during that window gets rejected with "Not authenticated" and
        // the agent never appears in the gateway's whisper registry —
        // which is why COACH_AGENT_REGISTERED was never showing up in
        // logs. The backend emits 'active-streams' at the end of
        // handleConnection (after auth + map population), so that's the
        // safe signal to fire agent-register.
        let agentRegisterSent = false;
        const sendAgentRegister = () => {
          if (agentRegisterSent) return;
          agentRegisterSent = true;
          socket?.emit("agent-register");
        };
        socket.on("active-streams", () => sendAgentRegister());
        socket.on("connect", () => {
          agentRegisterSent = false; // allow re-fire on reconnect
          // Belt-and-suspenders fallback in case 'active-streams' ever
          // stops firing — auth completes well within 600ms on healthy
          // paths.
          setTimeout(sendAgentRegister, 600);
        });

        receiver = startWhisperReceiver(socket);
      } catch {
        // Best-effort: if WS or AudioContext fails, we silently skip whisper
        // reception. Agent's call still works normally.
      }
    })();

    return () => {
      cancelled = true;
      try { receiver?.stop(); } catch { /* ignore */ }
      try { socket?.emit("agent-unregister"); } catch { /* ignore */ }
      try { socket?.disconnect(); } catch { /* ignore */ }
    };
  }, [userId]);

  // Whisper ducking — when supervisor whisper audio is actively playing on
  // the agent's machine, ramp the live call's speaker gain down so the
  // supervisor's voice cuts through without the agent having to strain to
  // hear it. Restore on whisper end. Pure radio-DJ ducking — manager voice
  // stays full volume; the lead's voice briefly steps aside.
  //
  // We use the existing speakerGainNodeRef (Web Audio gain node already
  // wired into the Twilio remote audio pipeline). On iOS the pipeline uses
  // audioElement.volume directly — same idea, different setter.
  useEffect(() => {
    const DUCK_TARGET = 0.4;          // 40% of current volume during whisper
    const RAMP_DOWN_S = 0.08;         // 80ms ramp into duck
    const RAMP_UP_S = 0.18;           // 180ms ramp back
    let savedSpeakerGain: number | null = null;
    let savedHTMLVolume: number | null = null;

    const handleWhisperActive = () => {
      const node = speakerGainNodeRef.current;
      const audioEl = twilioRemoteAudioRef.current;
      if (node) {
        if (savedSpeakerGain == null) savedSpeakerGain = node.gain.value;
        try {
          const target = (savedSpeakerGain ?? 1) * DUCK_TARGET;
          node.gain.cancelScheduledValues(node.context.currentTime);
          node.gain.setValueAtTime(node.gain.value, node.context.currentTime);
          node.gain.linearRampToValueAtTime(target, node.context.currentTime + RAMP_DOWN_S);
        } catch { node.gain.value = (savedSpeakerGain ?? 1) * DUCK_TARGET; }
      } else if (audioEl) {
        // iOS path — no Web Audio pipeline, just adjust HTMLAudioElement.volume
        if (savedHTMLVolume == null) savedHTMLVolume = audioEl.volume;
        audioEl.volume = Math.max(0, (savedHTMLVolume ?? 1) * DUCK_TARGET);
      }
    };

    const handleWhisperInactive = () => {
      const node = speakerGainNodeRef.current;
      const audioEl = twilioRemoteAudioRef.current;
      if (node && savedSpeakerGain != null) {
        try {
          node.gain.cancelScheduledValues(node.context.currentTime);
          node.gain.setValueAtTime(node.gain.value, node.context.currentTime);
          node.gain.linearRampToValueAtTime(savedSpeakerGain, node.context.currentTime + RAMP_UP_S);
        } catch { node.gain.value = savedSpeakerGain; }
        savedSpeakerGain = null;
      } else if (audioEl && savedHTMLVolume != null) {
        audioEl.volume = savedHTMLVolume;
        savedHTMLVolume = null;
      }
    };

    window.addEventListener('coach:whisper-active', handleWhisperActive as EventListener);
    window.addEventListener('coach:whisper-inactive', handleWhisperInactive as EventListener);
    return () => {
      window.removeEventListener('coach:whisper-active', handleWhisperActive as EventListener);
      window.removeEventListener('coach:whisper-inactive', handleWhisperInactive as EventListener);
      // Safety restore if unmount happens mid-duck
      try { handleWhisperInactive(); } catch { /* ignore */ }
    };
  }, []);

  // Keep activeCallSid ref in sync with state for use in Supabase callbacks
  useEffect(() => {
    activeCallSidRef.current = activeCallSid;
  }, [activeCallSid]);

  // Keep activeCall ref in sync with state for use in Supabase callbacks
  useEffect(() => {
    activeCallRef.current = activeCall;
  }, [activeCall]);

  // Set/clear active call flag in sessionStorage for SessionValidator protection.
  // This ensures isUserOnActiveCall() returns true even for manual calls where
  // useAutoStatusManager's on_call status update may be delayed.
  useEffect(() => {
    try {
      if (activeCall) {
        sessionStorage.setItem('insuracentral_active_call', 'true');
        localStorage.setItem('insuracentral_active_call', 'true');
      } else {
        sessionStorage.removeItem('insuracentral_active_call');
        localStorage.removeItem('insuracentral_active_call');
      }
    } catch {}
  }, [activeCall]);

  // Keep isInMultiLineMode ref in sync with state for use in device.on("incoming") handler
  useEffect(() => {
    isInMultiLineModeRef.current = isInMultiLineMode;
  }, [isInMultiLineMode]);

  // Mirror callDuration + callMetadata into refs so the call.on("disconnect")
  // handler can read the latest values without stale-closure issues.
  useEffect(() => {
    callDurationRef.current = callDuration;
  }, [callDuration]);
  useEffect(() => {
    callMetadataRef.current = callMetadata;
  }, [callMetadata]);

  // Keep isInPowerDialerMode ref in sync with state for use in device.on("incoming") handler
  const isInPowerDialerModeRef = useRef(false);
  useEffect(() => {
    isInPowerDialerModeRef.current = isInPowerDialerMode;
  }, [isInPowerDialerMode]);

  // Keep parentCallSid ref in sync with callMetadata for use in Supabase callbacks
  useEffect(() => {
    parentCallSidRef.current = callMetadata?.parentCallSid || null;
  }, [callMetadata?.parentCallSid]);

  // Wake Lock API - prevents screen from sleeping during calls (iOS/iPad fix)
  // This helps prevent call drops when the device screen would normally turn off
  const requestWakeLock = useCallback(async () => {
    if ('wakeLock' in navigator) {
      try {
        // Release any existing wake lock first
        if (wakeLockRef.current) {
          await wakeLockRef.current.release();
          wakeLockRef.current = null;
        }
        // Request new wake lock
        wakeLockRef.current = await navigator.wakeLock.request('screen');
        console.log('[TwilioDevice] Wake Lock acquired - screen will stay on during call');

        // Re-acquire wake lock if it gets released (e.g., when tab becomes visible again)
        wakeLockRef.current.addEventListener('release', () => {
          console.log('[TwilioDevice] Wake Lock released');
        });
      } catch (err) {
        // Wake Lock not supported or failed (e.g., low battery mode on iOS)
        console.warn('[TwilioDevice] Wake Lock request failed:', err);
      }
    }
  }, []);

  const releaseWakeLock = useCallback(async () => {
    if (wakeLockRef.current) {
      try {
        await wakeLockRef.current.release();
        wakeLockRef.current = null;
        console.log('[TwilioDevice] Wake Lock released - screen can sleep');
      } catch (err) {
        console.warn('[TwilioDevice] Wake Lock release failed:', err);
      }
    }
  }, []);

  // Re-acquire wake lock AND verify device state when tab becomes visible again.
  // Safari/iOS aggressively suspends WebSocket connections when the tab is hidden,
  // which can silently kill the Twilio signaling channel. When the user comes back,
  // we need to both re-acquire the wake lock AND check if the device is still alive.
  // NOTE: We use a ref for attemptAutoReconnect because it's defined later in the file
  // and can't be referenced directly here due to block-scoping rules.
  const attemptAutoReconnectRef = useRef<(() => void) | null>(null);
  const initializeDeviceRef = useRef<(() => Promise<void>) | null>(null);
  const initInFlightRef = useRef<{ at: number; run: Promise<void> } | null>(null);
  // The device this provider built, registered or not. SessionGate remounts the provider while a
  // session re-checks; a build left alive stayed logged in next to the remount's, and Telnyx
  // allows one login, so they logged each other out. Unmount shuts it down.
  const builtDeviceRef = useRef<any>(null);
  const unmountedRef = useRef(false);
  useEffect(() => {
    const handleVisibilityChange = async () => {
      if (document.visibilityState !== 'visible') return;

      // Re-acquire wake lock if we have an active call
      if (activeCall) {
        await requestWakeLock();
      }

      // Check if device went stale while tab was hidden — trigger reconnect if needed
      if (device && device.state !== 'registered') {
        console.warn(`[TwilioDevice] Tab visible — device state is '${device.state}', triggering reconnect`);
        // Don't wait out the current backoff (up to 60s in persistent mode):
        // the agent is looking at the app NOW. attemptAutoReconnect() returns
        // early while a retry is pending, so clear it first.
        reconnectAttemptRef.current = 0;
        if (reconnectTimeoutRef.current) {
          clearTimeout(reconnectTimeoutRef.current);
          reconnectTimeoutRef.current = null;
        }
        attemptAutoReconnectRef.current?.();
      }

      // Resume any suspended AudioContexts (Safari pauses them in background)
      if (micAudioContextRef.current?.state === 'suspended') {
        micAudioContextRef.current.resume().catch(() => {});
      }
      if (speakerAudioContextRef.current?.state === 'suspended') {
        speakerAudioContextRef.current.resume().catch(() => {});
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [activeCall, device, requestWakeLock]);

  // Safari: Suppress unhandled "NotAllowedError" rejections from Twilio SDK's internal
  // setSinkId() calls. Safari requires a user gesture for setSinkId() even with sinkId='default'.
  // Twilio's sound.js and peerconnection.js call setSinkId internally and don't catch the
  // rejection on Safari, causing unhandled promise rejection errors in PostHog/error tracking.
  // This handler only suppresses setSinkId-related NotAllowedError — all other errors propagate.
  useEffect(() => {
    if (!isSafariBrowser) return;

    const handleUnhandledRejection = (event: PromiseRejectionEvent) => {
      const error = event.reason;
      if (
        error instanceof DOMException &&
        error.name === 'NotAllowedError' &&
        error.message?.includes('user gesture')
      ) {
        event.preventDefault();
        console.debug('[TwilioDevice] Suppressed Safari setSinkId NotAllowedError');
      }
    };

    window.addEventListener('unhandledrejection', handleUnhandledRejection);
    return () => window.removeEventListener('unhandledrejection', handleUnhandledRejection);
  }, []);

  // Initialize ringback audio for outbound calls
  useEffect(() => {
    const audio = new Audio('/sounds/ringback.mp3');
    audio.loop = true;
    audio.volume = 0.15; // Subtle — agents are on calls all day, aggressive ringback fatigues
    // Don't advertise the ringback as resumable to external media controls.
    // Without this, the browser associates the audio with the system Media
    // Session, so pressing keyboard media keys (Play/Pause) restarts the
    // ringback mid-CRM-session. Users reported: "pressing Play on my keyboard
    // starts the ringing lol".
    (audio as any).disableRemotePlayback = true;
    ringbackAudioRef.current = audio;

    // Silence the Media Session play/pause action handlers for this tab so
    // keyboard media keys can't trigger or resume the ringback. A CRM dialer
    // has no legitimate use for OS-level play/pause anyway.
    if (typeof navigator !== 'undefined' && 'mediaSession' in navigator) {
      try {
        navigator.mediaSession.setActionHandler('play', () => {});
        navigator.mediaSession.setActionHandler('pause', () => {});
      } catch {
        // Older browsers may not support these action names; safe to ignore.
      }
    }

    return () => {
      if (ringbackAudioRef.current) {
        ringbackAudioRef.current.pause();
        ringbackAudioRef.current = null;
      }
      if (typeof navigator !== 'undefined' && 'mediaSession' in navigator) {
        try {
          navigator.mediaSession.setActionHandler('play', null);
          navigator.mediaSession.setActionHandler('pause', null);
        } catch {}
      }
    };
  }, []);

  // Listen for the `dialer:pickup-chime` window event — dispatched by
  // handleCustomerAnswered for manual + power dialers. Plays a single soft
  // notification chime so the agent audibly notices the customer picked up
  // instead of only looking at visual state. Multi-line has its own earlier
  // chime trigger; skips the event dispatch, so no double-fire.
  useEffect(() => {
    if (typeof window === 'undefined') return;
    const onPickup = () => {
      try { playPickupChime(); } catch {}
    };
    window.addEventListener('dialer:pickup-chime', onPickup);
    return () => window.removeEventListener('dialer:pickup-chime', onPickup);
  }, [playPickupChime]);

  // Play ringback tone (for outbound calls while waiting for customer to answer)
  // Ringback stops when Supabase realtime customer_answered event is received
  // Max 30 second timeout as fallback safety
  const playRingback = useCallback(() => {
    // Clear any existing timeout
    if (ringbackTimeoutRef.current) {
      clearTimeout(ringbackTimeoutRef.current);
      ringbackTimeoutRef.current = null;
    }

    if (ringbackAudioRef.current) {
      // Guard: if already playing, don't restart. Multiple call sites (batch start,
      // reconnect, retry) can invoke playRingback for the same dial round. Without
      // this check, the audio resets to t=0 each time → Brandon heard overlapping
      // ringback sounds ("sounded like dialing 7 numbers at once").
      if (!ringbackAudioRef.current.paused) return;

      ringbackAudioRef.current.currentTime = 0;
      ringbackAudioRef.current.play().catch(() => {
        // Failed to play ringback
      });

      // Max 30 second timeout - stop ringing after 30s regardless
      ringbackTimeoutRef.current = setTimeout(() => {
        if (ringbackAudioRef.current) {
          ringbackAudioRef.current.pause();
          ringbackAudioRef.current.currentTime = 0;
        }
        ringbackTimeoutRef.current = null;
      }, 30000);
    }
  }, []);

  // Stop ringback tone
  const stopRingback = useCallback(() => {
    // Clear timeout when stopping
    if (ringbackTimeoutRef.current) {
      clearTimeout(ringbackTimeoutRef.current);
      ringbackTimeoutRef.current = null;
    }

    if (ringbackAudioRef.current) {
      ringbackAudioRef.current.pause();
      ringbackAudioRef.current.currentTime = 0;
    }

    // Tell the Media Session nothing is playing so keyboard media keys don't
    // light up a paused-state indicator that resumes ringback on next press.
    if (typeof navigator !== 'undefined' && 'mediaSession' in navigator) {
      try {
        navigator.mediaSession.playbackState = 'none';
      } catch {}
    }
  }, []);

  // ============== Call Session Persistence for Page Refresh Recovery ==============

  // Save active call session to localStorage for recovery after page refresh
  const persistCallSession = useCallback((session: PersistedCallSession) => {
    try {
      localStorage.setItem(ACTIVE_CALL_SESSION_KEY, JSON.stringify(session));
      console.log('[TwilioDevice] Call session persisted for recovery:', session.callSid);
    } catch (error) {
      console.warn('[TwilioDevice] Failed to persist call session:', error);
    }
  }, []);

  // Clear persisted call session (called on disconnect or recovery failure)
  const clearPersistedCallSession = useCallback(() => {
    try {
      localStorage.removeItem(ACTIVE_CALL_SESSION_KEY);
      console.log('[TwilioDevice] Persisted call session cleared');
    } catch (error) {
      console.warn('[TwilioDevice] Failed to clear persisted call session:', error);
    }
  }, []);

  // Get persisted call session from localStorage
  const getPersistedCallSession = useCallback((): PersistedCallSession | null => {
    try {
      const stored = localStorage.getItem(ACTIVE_CALL_SESSION_KEY);
      if (!stored) return null;

      const session = JSON.parse(stored) as PersistedCallSession;

      // Check if session is too old (> 5 minutes = likely call ended)
      const sessionAge = Date.now() - session.startTime;
      const maxAge = 5 * 60 * 1000; // 5 minutes

      if (sessionAge > maxAge) {
        console.log('[TwilioDevice] Persisted session too old, clearing:', sessionAge / 1000, 'seconds');
        clearPersistedCallSession();
        return null;
      }

      return session;
    } catch (error) {
      console.warn('[TwilioDevice] Failed to read persisted call session:', error);
      return null;
    }
  }, [clearPersistedCallSession]);

  // Attempt to recover a call session after page refresh
  const attemptCallRecovery = useCallback(async (twilioDevice: Device): Promise<boolean> => {
    // Prevent multiple recovery attempts
    if (hasAttemptedRecoveryRef.current) {
      return false;
    }
    hasAttemptedRecoveryRef.current = true;

    const session = getPersistedCallSession();
    if (!session) {
      return false;
    }

    console.log('[TwilioDevice] Attempting call recovery for:', session.callSid);
    setIsRecoveringCall(true);
    setRecoveryError(null);

    try {
      // Check if the call/conference is still active on the backend
      const response = await api.get({
        url: getApiUrl(`/communication/calls/${session.callSid}/status`),
        auth: true,
      });

      const statusData = (response.data as any)?.data;

      // Check if call is still in progress
      if (!statusData || statusData.status === 'completed' || statusData.status === 'failed' || statusData.status === 'canceled') {
        console.log('[TwilioDevice] Call already ended, clearing session:', statusData?.status);
        clearPersistedCallSession();
        setIsRecoveringCall(false);
        return false;
      }

      // Request microphone permission before reconnecting
      const hasPermission = await requestMicrophonePermission();
      if (!hasPermission) {
        console.warn('[TwilioDevice] Microphone permission denied during recovery');
        setRecoveryError('Microphone permission required to rejoin call');
        setIsRecoveringCall(false);
        return false;
      }

      // Rejoin the conference
      console.log('[TwilioDevice] Rejoining conference:', session.conferenceName);

      // DialIQ predictive sessions use the `predictive:` prefix so the
      // backend voice() handler returns TwiML with the right conference
      // properties (startConferenceOnEnter:true for the agent reclaiming
      // an idle conference). Without this distinction the existing
      // `conference:` branch returns startConferenceOnEnter:false — the
      // agent would join but the conference wouldn't "start," leaving
      // them in silence.
      const isPredictiveConference =
        typeof session.conferenceName === 'string' &&
        session.conferenceName.startsWith('predictive-');

      const callParams: Record<string, string> = {
        To: isPredictiveConference
          ? `predictive:${session.conferenceName}`
          : `conference:${session.conferenceName}`,
      };

      if (session.leadId) {
        callParams.LeadId = session.leadId;
      }

      callParams.isRecovery = 'true';

      const call = await twilioDevice.connect({
        params: callParams,
        rtcConstraints: {
          audio: {
            echoCancellation: true,
            noiseSuppression: true,
            autoGainControl: true,
          }
        }
      });

      // Set up the recovered call
      setActiveCall(call);
      setActiveCallSid(session.callSid);
      setCallMetadata({
        leadName: session.leadName,
        leadId: session.leadId,
        phoneNumber: session.phoneNumber,
        source: session.source,
      });
      setCurrentConferenceName(session.conferenceName);
      setCallStatus("in-progress");

      // Calculate elapsed duration
      const elapsedSeconds = Math.floor((Date.now() - session.startTime) / 1000);
      setCallDuration(elapsedSeconds);

      // Start duration timer
      if (durationIntervalRef.current) {
        clearInterval(durationIntervalRef.current);
      }
      durationIntervalRef.current = setInterval(() => {
        setCallDuration((prev) => prev + 1);
      }, 1000);

      // Set up call listeners
      setupCallListeners(call);

      // Request wake lock
      requestWakeLock();

      toast({
        title: "Call Reconnected",
        description: "Successfully rejoined your active call",
      });

      console.log('[TwilioDevice] Call recovery successful');
      setIsRecoveringCall(false);
      return true;
    } catch (error: any) {
      console.error('[TwilioDevice] Call recovery failed:', error);
      setRecoveryError(error.message || 'Failed to reconnect to call');
      clearPersistedCallSession();
      setIsRecoveringCall(false);

      toast({
        title: "Call Recovery Failed",
        description: "Could not reconnect to your previous call. It may have ended.",
        variant: "destructive",
      });

      return false;
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [getPersistedCallSession, clearPersistedCallSession, requestWakeLock, toast]);

  // ============== End Call Session Persistence ==============

  // Timeout wrapper for async operations (iOS Safari can hang fetch/WebSocket ops when backgrounded)
  const withTimeout = <T,>(promise: Promise<T>, ms = 8000, label = 'Operation'): Promise<T> => {
    return Promise.race([
      promise,
      new Promise<never>((_, reject) =>
        setTimeout(() => reject(new Error(`${label} timed out after ${ms}ms`)), ms)
      ),
    ]);
  };

  // Fetch Twilio access token from backend
  const fetchAccessToken = useCallback(async (): Promise<{ token: string; expiresAt: string; identity: string; provider?: string } | null> => {
    try {
      const response = await withTimeout(
        api.get({
          url: getApiUrl(endpoint.communication.getVoiceToken),
          auth: true,
          // A query param: a new header would fail the API's CORS preflight for everyone.
          options: { params: { slot: await phoneSlot() } },
        }),
        8000,
        'Token fetch'
      );

      // Parse the backend response structure
      const responseData = response.data as any;

      // Backend can return in two formats:
      // 1. Direct format: { identity, token, expires_at }
      // 2. Nested format: { statusCode, message, data: { identity, token, expires_at } }

      let tokenData = responseData;

      // If response has nested data property, use that
      if (responseData?.data?.token) {
        tokenData = responseData.data;
      }

      if (tokenData?.token && tokenData?.expires_at && tokenData?.identity) {
        lastTokenRefreshRef.current = Date.now();
        return {
          token: tokenData.token,
          expiresAt: tokenData.expires_at,
          identity: tokenData.identity,
          provider: tokenData.provider,
        };
      }

      if (inStartupRecoveryWindow()) {
        console.warn('[TwilioDevice] invalid token payload during startup; recovery will retry');
      } else {
        toast({
          title: "Token Fetch Failed",
          description: "Received invalid token data from server",
          variant: "destructive",
        });
      }
      return null;
    } catch (error: any) {
      // A 403 here is the MFA gate, not a fault — expected until the session is stamped.
      if (inStartupRecoveryWindow()) {
        console.warn('[TwilioDevice] token fetch failed during startup; recovery will retry:', error?.message);
      } else {
        toast({
          title: "Token Fetch Failed",
          description: error?.message || "Failed to fetch voice token from server",
          variant: "destructive",
        });
      }
      return null;
    }
  }, [toast, inStartupRecoveryWindow]);

  // Schedule token refresh 5 minutes before expiration
  // Uses deviceRef.current inside timeouts to avoid stale closure over `device`
  const scheduleTokenRefresh = useCallback((expiresAt: string) => {
    // Clear any existing refresh timeout
    if (tokenRefreshTimeoutRef.current) {
      clearTimeout(tokenRefreshTimeoutRef.current);
      tokenRefreshTimeoutRef.current = null;
    }

    const expirationTime = new Date(expiresAt).getTime();
    const currentTime = Date.now();
    const timeUntilExpiry = expirationTime - currentTime;

    // Refresh 5 minutes (300000ms) before expiration
    const refreshTime = timeUntilExpiry - 300000;

    if (refreshTime > 0) {
      tokenRefreshTimeoutRef.current = setTimeout(async () => {
        try {
          const tokenData = await fetchAccessToken();
          const currentDevice = deviceRef.current;
          if (tokenData && currentDevice) {
            if (carrierChanged(currentDevice, tokenData)) return;
            await currentDevice.updateToken(tokenData.token);
            // Schedule next refresh
            scheduleTokenRefresh(tokenData.expiresAt);
          } else {

            toast({
              title: "Token Refresh Failed",
              description: "Failed to refresh voice token. You may need to reload.",
              variant: "destructive",
            });
          }
        } catch (error) {

          toast({
            title: "Token Refresh Error",
            description: "An error occurred while refreshing the voice token",
            variant: "destructive",
          });
        }
      }, refreshTime);
    } else {
      // Token is already expired or will expire very soon

      fetchAccessToken().then((tokenData) => {
        const currentDevice = deviceRef.current;
        if (tokenData && currentDevice) {
          if (carrierChanged(currentDevice, tokenData)) return;
          currentDevice.updateToken(tokenData.token);
          scheduleTokenRefresh(tokenData.expiresAt);
        }
      });
    }
  }, [fetchAccessToken, toast]);

  // Request microphone permission (call this before making/answering calls)
  const requestMicrophonePermission = useCallback(async (): Promise<boolean> => {
    try {
      const { requestMicrophoneWithGuidance } = await import("@/app/_module/utils/microphone-permission");
      const permissionResult = await requestMicrophoneWithGuidance();

      if (!permissionResult.granted) {
        toast({
          title: "Microphone Access Required",
          description: permissionResult.error || "Microphone access is required to make and receive calls.",
          variant: "destructive",
        });
        return false;
      }
      return true;
    } catch (error) {

      return false;
    }
  }, [toast]);

  // Clean up a Twilio Device: remove all listeners, then destroy
  // Prevents orphaned event listeners when reconnecting or unmounting
  const cleanupDevice = useCallback((deviceToCleanup: Device) => {
    try {
      deviceToCleanup.removeAllListeners('registered');
      deviceToCleanup.removeAllListeners('unregistered');
      deviceToCleanup.removeAllListeners('error');
      deviceToCleanup.removeAllListeners('incoming');
      deviceToCleanup.removeAllListeners('tokenWillExpire');
    } catch { /* ignore listener removal errors */ }
    try {
      deviceToCleanup.destroy();
    } catch { /* ignore destroy errors */ }
  }, []);

  // CARRIER CHANGE MID-SESSION: an agent's carrier can change while the app is open (a first
  // number bought on the other carrier, the default number moved to it). That carrier's token
  // can't go on this device, so every refresh path asks carrierChanged() before updateToken:
  // false = same carrier, carry on as before; true = leave this device alone. It's destroyed once
  // no call is up and the recovery effect builds the new one (initializeDevice).
  const carrierSwitchRef = useRef<'pending' | 'rebuild' | 'building' | null>(null);
  const carrierRecheckRef = useRef<NodeJS.Timeout | null>(null);
  const switchCarrierWhenIdle = () => {
    if (carrierRecheckRef.current) clearTimeout(carrierRecheckRef.current);
    carrierRecheckRef.current = null;
    if (carrierSwitchRef.current !== 'pending') return;
    if (activeCallRef.current || activeCallSidRef.current || coachBargeCallRef.current || deviceRef.current?.calls.length) {
      // a barge leg or a ringing call can end without a state change here: look again shortly
      carrierRecheckRef.current = setTimeout(switchCarrierWhenIdle, 5000);
      return;
    }
    carrierSwitchRef.current = 'rebuild';
    if (deviceRef.current) cleanupDevice(deviceRef.current);
    deviceRef.current = null;
    setDevice(null);
    setIsDeviceReady(false);
    if (tokenRefreshTimeoutRef.current) clearTimeout(tokenRefreshTimeoutRef.current);
    tokenRefreshTimeoutRef.current = null;
    if (reconnectTimeoutRef.current) clearTimeout(reconnectTimeoutRef.current);
    reconnectTimeoutRef.current = null;
    reconnectAttemptRef.current = 0;
    setIsReconnecting(false);
  };
  const carrierChanged = (dev: Device, tokenData: { provider?: string }) => {
    if (isDemoMode() || (tokenData.provider === 'telnyx') === (dev instanceof TelnyxDevice)) {
      // back on this device's carrier before the switch ran: call it off
      if (dev === deviceRef.current && carrierSwitchRef.current === 'pending') {
        carrierSwitchRef.current = null;
        if (carrierRecheckRef.current) clearTimeout(carrierRecheckRef.current);
        carrierRecheckRef.current = null;
      }
      return false;
    }
    if (dev === deviceRef.current && !carrierSwitchRef.current) {
      carrierSwitchRef.current = 'pending';
      switchCarrierWhenIdle();
    }
    return true;
  };
  // the call that held a switch back just ended
  useEffect(switchCarrierWhenIdle, [activeCall, activeCallSid]);

  // Soft reconnect: re-register existing device with a fresh token (no page reload needed)
  // This handles iPad/iOS WebSocket drops where the device goes red/unregistered
  const reconnectDevice = useCallback(async () => {
    if (isReconnecting) return;

    // DEMO MODE — there is no socket to drop and no token to refresh. Reconnecting
    // would call fetchAccessToken() against a Twilio account the sandbox doesn't use,
    // fail, and leave the widget stuck on "Network connection lost — reconnecting…".
    // Just assert ready.
    if (isDemoMode()) {
      setDeviceError(null);
      setIsDeviceReady(true);
      setIsReconnecting(false);
      return;
    }

    setIsReconnecting(true);
    setDeviceError(null);

    try {
      if (device) {
        // Existing device — fetch fresh token and re-register
        const tokenData = await fetchAccessToken();
        if (tokenData) {
          if (carrierChanged(device, tokenData)) return;
          device.updateToken(tokenData.token);
          await withTimeout(device.register(), 8000, 'Device register');
          scheduleTokenRefresh(tokenData.expiresAt);
          reconnectAttemptRef.current = 0;
          setIsReconnecting(false);
          toast({
            title: "Dialer Reconnected",
            description: "Connection restored successfully.",
          });
          return;
        }
      }

      // No existing device or token fetch failed — full re-init
      // Clean up old device listeners before destroying to prevent orphaned handlers
      if (device) {
        cleanupDevice(device);
        setDevice(null);
      }
      setIsDeviceReady(false);
      reconnectAttemptRef.current = 0;
      setIsReconnecting(false);
      // Re-initialize from scratch
      await initializeDevice();
    } catch (error: any) {
      setIsReconnecting(false);
      // A failed MANUAL tap used to park the dialer here until the next tap —
      // the background loop only resumed on a device/online/visibility event.
      // Hand it straight back to auto-reconnect so it keeps trying on its own.
      setDeviceError("Couldn't reconnect yet — retrying automatically in the background. You can also tap Reconnect or refresh.");
      toast({
        title: "Reconnect Failed",
        description: "Still retrying automatically. Tap Reconnect to try now, or refresh the page.",
        variant: "destructive",
      });
      reconnectAttemptRef.current = 0;
      attemptAutoReconnectRef.current?.();
    }
  }, [device, isReconnecting, toast, scheduleTokenRefresh]);

  // Auto-reconnect with exponential backoff (triggered by unregistered/error/heartbeat/visibility events)
  // Guard: skip if a reconnect timeout is already pending to prevent duplicate concurrent attempts
  const attemptAutoReconnect = useCallback(() => {
    if (reconnectTimeoutRef.current) return; // Already has a pending reconnect — don't stack

    // PERSISTENT MODE (2026-07-24): auto-reconnect used to STOP after 10
    // attempts (~3.5 min) — after that even wake/visibility events only
    // re-showed the error, so agents on flaky WiFi/iPads had to tap Reconnect
    // by hand ("I thought it auto reconnects us"). Now it NEVER gives up:
    // past the max it keeps retrying every 60s in the background (banner
    // shows it's auto-retrying; the toast fires once, not every cycle), and
    // the 'online' listener below snaps it back the moment the network
    // returns.
    const attempt = reconnectAttemptRef.current;
    const inPersistentMode = attempt >= MAX_RECONNECT_ATTEMPTS;
    if (inPersistentMode && attempt === MAX_RECONNECT_ATTEMPTS) {
      setDeviceError("Dialer disconnected — reconnecting automatically in the background…");
      announcedDisconnectRef.current = true;
      toast({
        title: "Dialer Disconnected",
        description: "Still reconnecting automatically — you can also tap Reconnect or refresh.",
        variant: "destructive",
      });
    }

    // Exponential backoff: 2s, 4s, 8s, 16s, 30s (cap); persistent mode: 60s
    const delay = inPersistentMode ? 60000 : Math.min(2000 * Math.pow(2, attempt), 30000);
    reconnectAttemptRef.current = attempt + 1;

    setIsReconnecting(true);

    reconnectTimeoutRef.current = setTimeout(async () => {
      reconnectTimeoutRef.current = null;
      try {
        if (device) {
          const tokenData = await fetchAccessToken();
          if (tokenData) {
            if (carrierChanged(device, tokenData)) return;
            device.updateToken(tokenData.token);
            // Twilio's register() THROWS if the device isn't in the
            // "unregistered" state. A device-level error doesn't always drop
            // registration (mic/permission/token-refresh codes), so with every
            // error now routed here, a still-registered device would throw,
            // retry into backoff forever and end in a false "Dialer
            // Disconnected". Already registered = already reconnected.
            if (device.state !== 'registered') {
              await withTimeout(device.register(), 8000, 'Device register');
            }
            scheduleTokenRefresh(tokenData.expiresAt);
            // Success — reset attempts
            reconnectAttemptRef.current = 0;
            setIsReconnecting(false);
            setIsDeviceReady(true);
            setDeviceError(null);
            if (announcedDisconnectRef.current) {
              announcedDisconnectRef.current = false;
              toast({
                title: "Dialer Reconnected",
                description: "Connection restored automatically.",
              });
            }
            return;
          }
        }
        // No device to re-register (app opened offline, or the device was torn
        // down): the loop used to spin on "try again" forever without ever
        // creating one — a dead dialer until refresh. Build it here instead.
        if (!device && !deviceRef.current) {
          await initializeDeviceRef.current?.();
          if (deviceRef.current) {
            reconnectAttemptRef.current = 0;
            setIsReconnecting(false);
            setDeviceError(null);
            return;
          }
        }
        // Token fetch failed, or init didn't come up — try again
        setIsReconnecting(false);
        attemptAutoReconnect();
      } catch {
        setIsReconnecting(false);
        attemptAutoReconnect();
      }
    }, delay);
  }, [device, toast, scheduleTokenRefresh]);

  // Keep ref in sync so the visibility change handler (defined earlier) can access it
  useEffect(() => {
    attemptAutoReconnectRef.current = attemptAutoReconnect;
  }, [attemptAutoReconnect]);

  // NETWORK-RETURN FAST PATH (2026-07-24): when the browser reports the
  // connection is back, don't wait out the current backoff — reset the
  // attempt budget and reconnect immediately.
  useEffect(() => {
    const onOnline = () => {
      reconnectAttemptRef.current = 0;
      if (reconnectTimeoutRef.current) {
        clearTimeout(reconnectTimeoutRef.current);
        reconnectTimeoutRef.current = null;
      }
      attemptAutoReconnectRef.current?.();
    };
    window.addEventListener('online', onOnline);
    return () => window.removeEventListener('online', onOnline);
  }, []);

  // NATIVE APP WAKE (iOS/Android): while the app is backgrounded iOS freezes
  // the WebView and kills the Twilio signaling socket, but `device.state` can
  // still read 'registered' — so a state check alone leaves the agent with a
  // dialer that looks fine and can't take a call. On every foreground we mint
  // a fresh token and re-register outright, and we never sit out a pending
  // backoff. A live call is left alone.
  const forceReRegister = useCallback(async () => {
    const dev = deviceRef.current;
    if (!dev) {
      attemptAutoReconnectRef.current?.();
      return;
    }
    try {
      const tokenData = await fetchAccessToken();
      if (!tokenData) {
        attemptAutoReconnectRef.current?.();
        return;
      }
      if (carrierChanged(dev, tokenData)) return;
      dev.updateToken(tokenData.token);
      if (dev.state !== 'registered') {
        await withTimeout(dev.register(), 8000, 'Device register');
      }
      scheduleTokenRefresh(tokenData.expiresAt);
      reconnectAttemptRef.current = 0;
      setIsReconnecting(false);
      setDeviceError(null);
      announcedDisconnectRef.current = false;
    } catch {
      // Fall back to the normal backoff loop.
      attemptAutoReconnectRef.current?.();
    }
  }, [fetchAccessToken, scheduleTokenRefresh]);

  useEffect(() => {
    let remove: (() => void) | undefined;
    let cancelled = false;
    (async () => {
      try {
        const { Capacitor } = await import('@capacitor/core');
        if (!Capacitor.isNativePlatform()) return;
        const { App } = await import('@capacitor/app');
        const handle = await App.addListener('appStateChange', ({ isActive }) => {
          if (!isActive) return;
          if (activeCallRef.current) return; // mid-call: leave the connection alone
          if (reconnectTimeoutRef.current) {
            clearTimeout(reconnectTimeoutRef.current);
            reconnectTimeoutRef.current = null;
          }
          reconnectAttemptRef.current = 0;
          void forceReRegister();
        });
        if (cancelled) { void handle.remove(); return; }
        remove = () => { void handle.remove(); };
      } catch {
        /* web, or the plugin isn't available — visibility + online still cover it */
      }
    })();
    return () => { cancelled = true; remove?.(); };
  }, [forceReRegister]);

  // Dialer system switched (default number moved to InsuraDialer Classic/Next): ask for a
  // token now instead of at the next refresh, so carrierChanged() swaps the device right away.
  // Mid-call it waits: the next refresh catches it, as before.
  useEffect(() => {
    const onCheck = () => {
      if (!activeCallRef.current) void forceReRegister();
    };
    window.addEventListener("dialer:carrier-check", onCheck);
    return () => window.removeEventListener("dialer:carrier-check", onCheck);
  }, [forceReRegister]);

  // Heartbeat: Periodically verify Twilio Device is still registered and refresh token proactively.
  // Safari (desktop & mobile) aggressively suspends WebSocket connections in background tabs,
  // which silently kills the Twilio signaling channel. This interval detects stale connections
  // and auto-reconnects before the agent notices. Also refreshes the token every 30 min on
  // Safari/mobile to prevent expiration during long power-dialing sessions.
  const heartbeatIntervalRef = useRef<NodeJS.Timeout | null>(null);
  useEffect(() => {
    if (!device || !isDeviceReady) {
      if (heartbeatIntervalRef.current) {
        clearInterval(heartbeatIntervalRef.current);
        heartbeatIntervalRef.current = null;
      }
      return;
    }

    // Check every 30 seconds
    heartbeatIntervalRef.current = setInterval(async () => {
      if (!device) return;

      const state = device.state;
      // Device should be 'registered' — if it's 'unregistered' or 'destroyed', reconnect
      if (state !== 'registered') {
        console.warn(`[TwilioDevice] Heartbeat detected device state: ${state} — triggering reconnect`);
        attemptAutoReconnect();
        return;
      }

      // On Safari, proactively refresh token every 30 min (this interval fires every 30s,
      // so we use a ref to track the last refresh time)
      if (isSafariBrowser && lastTokenRefreshRef.current) {
        const timeSinceRefresh = Date.now() - lastTokenRefreshRef.current;
        if (timeSinceRefresh > 30 * 60 * 1000) { // 30 minutes
          try {
            const tokenData = await fetchAccessToken();
            if (tokenData && device) {
              if (carrierChanged(device, tokenData)) return;
              await device.updateToken(tokenData.token);
              scheduleTokenRefresh(tokenData.expiresAt);
              lastTokenRefreshRef.current = Date.now();
              console.log('[TwilioDevice] Proactive Safari token refresh completed');
            }
          } catch {
            console.warn('[TwilioDevice] Proactive token refresh failed — will retry next heartbeat');
          }
        }
      }
    }, 30000);

    return () => {
      if (heartbeatIntervalRef.current) {
        clearInterval(heartbeatIntervalRef.current);
        heartbeatIntervalRef.current = null;
      }
    };
  }, [device, isDeviceReady, attemptAutoReconnect, scheduleTokenRefresh]);

  // Initialize Twilio Device (without requiring microphone permission)
  const initializeDevice = useCallback(async () => {
    if (!planHasPhoneRef.current) return; // no phone on this plan — nothing to start
    if (device) {
      return;
    }
    // One build at a time. A slow token fetch let the retry timer and 'mfa-satisfied' start
    // several at once, each registering its own device; Telnyx logs every earlier one out, so
    // they knocked each other offline. 30 s cap so a stuck build can't block recovery.
    const inFlight = initInFlightRef.current;
    if (inFlight && Date.now() - inFlight.at < 30_000) return inFlight.run;
    let finishRun!: () => void;
    const run = new Promise<void>((resolve) => { finishRun = resolve; });
    initInFlightRef.current = { at: Date.now(), run };

    setIsDeviceRegistering(true);
    setDeviceError(null);

    try {
      // CRITICAL: Send heartbeat to update lastSeenAt BEFORE registering device
      // This prevents calls from being auto-forwarded when user just loaded the page
      // The heartbeat updates lastSeenAt in the database so backend knows user is online
      try {
        const supabase = await getSupabaseClient();
        const { data: sessionData } = await supabase.auth.getSession();
        if (sessionData?.session?.access_token) {
          const heartbeatUrl = getApiUrl('users/heartbeat');
          // Raw fetch bypasses the shared axios config, so the device id has to
          // be attached by hand. Without it the MFA API gate refuses the request
          // (a missing X-Device-Id is a denial, not a free pass) and the
          // heartbeat 403s for every MFA-enabled user.
          const { getOrCreateDeviceId } = await import('@/app/_module/services/auth');
          await fetch(heartbeatUrl, {
            method: 'POST',
            headers: {
              'Authorization': `Bearer ${sessionData.session.access_token}`,
              'Content-Type': 'application/json',
              'X-Device-Id': getOrCreateDeviceId(),
            },
          });
        }
      } catch {
        // Ignore errors - continue with device initialization
      }

      // DEMO MODE — skip the Twilio token entirely. Nothing to fetch because nothing
      // will be dialed.
      const tokenData = isDemoMode()
        ? { token: 'demo', expiresAt: new Date(Date.now() + 24 * 60 * 60 * 1000).toISOString() }
        : await fetchAccessToken();
      if (!tokenData) {
        throw new Error("Failed to obtain access token");
      }

      // DEMO MODE swaps in a simulator implementing the same Device/Call surface this
      // file uses, so every dialer behaves normally — calls ring, connect, run a timer,
      // drive the audio meter, mute, send DTMF and hang up — while no WebRTC opens, no
      // number is dialed and nothing is billed. All the event wiring below is shared,
      // which is what keeps the demo honest: it exercises the real code paths.
      // Build-time flag, so it can never switch on in production by accident.
      // A Telnyx voice token gets the same surface over TelnyxRTC (telnyx-device-adapter).
      const twilioDevice: any = isDemoMode() ? createDemoDevice()
        : (tokenData as any).provider === 'telnyx' ? await createTelnyxDevice(tokenData.token)
        : new Device(tokenData.token, {
        logLevel: 0, // 0 = silent (no console logs), 1 = errors and warnings, 2 = all logs
        codecPreferences: [Call.Codec.Opus, Call.Codec.PCMU],
        enableImprovedSignalingErrorPrecision: true,
        // Allow Twilio to use default input/output devices
        // This ensures proper microphone selection
        allowIncomingWhileBusy: false,
        // Close protection prevents accidental call disconnect when navigating away
        closeProtection: true,
        // Edge selection — 'roaming' lets Twilio resolve the geographically
        // NEAREST edge per client, with the US edges kept as explicit fallbacks.
        //
        // This was ['ashburn', 'umatilla'] — both US — which is right for agents
        // in the States and bad for everyone else. Overseas dialers (the Kosovo
        // team on MEI) had their WebRTC audio hauled ~7,000 km to Virginia before
        // it ever reached the PSTN, adding ~120-160ms RTT plus the jitter and loss
        // that come with a transatlantic hop. Prospects heard them poorly while
        // the same mic sounded fine on Discord, which picks a European edge.
        //
        // Order matters: roaming first, then the previous US edges so behaviour
        // is unchanged for US agents and degrades to the old config if roaming
        // can't be resolved.
        edge: ['roaming', 'ashburn', 'umatilla'],
      });
      builtDeviceRef.current = twilioDevice;
      if (unmountedRef.current) {
        cleanupDevice(twilioDevice); // the provider went away while this build fetched its token
        return;
      }

      // Kill the SDK's BUILT-IN incoming ringtone. IncomingCallContext is the
      // one true ringer (broadcast-driven, with call-waiting chime rules) —
      // the SDK sound both double-rings real calls and produces ghost 1-2s
      // ring bursts at login/registration, when Twilio hands a newly
      // registered client a queued incoming call that cancels moments later.
      try { (twilioDevice as any).audio?.incoming(false); } catch { /* demo device */ }

      // Schedule token refresh before expiration (demo has no token to refresh)
      if (!isDemoMode()) scheduleTokenRefresh(tokenData.expiresAt);

      // Device event listeners
      twilioDevice.on("registered", async () => {
        setIsDeviceReady(true);
        setIsDeviceRegistering(false);

        // Attempt to recover any active call session from before page refresh
        // This allows users to rejoin calls if they accidentally refresh the page
        await attemptCallRecovery(twilioDevice);
      });

      twilioDevice.on("error", (error: any) => {
        // Every device-level error goes to the reconnect loop. The old switch
        // only reconnected on 4 codes and TOASTED "Dialer Error … try refreshing
        // the page" for everything else — which on iOS was the routine signaling
        // drop after backgrounding (31005/31009/53001 and friends): scary, and
        // wrong, since the loop recovers it in seconds. The loop owns the
        // messaging: one "Dialer Disconnected" after it has really failed, one
        // "Reconnected" after that. In-call errors are handled on the call.
        const errorCode = error?.code || error?.twilioError?.code;
        console.warn(`[TwilioDevice] device error ${errorCode ?? "?"}: ${error?.message ?? ""} — reconnecting`);
        setDeviceError("Dialer connection dropped — reconnecting automatically…");
        setIsDeviceReady(false);
        attemptAutoReconnect();
      });

      twilioDevice.on("incoming", async (call: any) => {
        // SUPERVISOR BARGE auto-accept. The manager just clicked Barge, which
        // dialed their browser Client into the conference. We ONLY auto-answer
        // the exact leg whose CallSid matches the participant the backend just
        // created (expectedSid) — never a random incoming lead call. We wait
        // briefly for expectedSid (the /coach/barge/join response) in case the
        // ring beats it; if it never matches, we fall through to normal routing.
        {
          const pending = coachBargeRef.current;
          if (pending && Date.now() < pending.expiry) {
            const incomingSid = (call.parameters as any)?.CallSid as string | undefined;
            let expected = pending.expectedSid;
            for (let i = 0; !expected && i < 100; i++) {
              const cur = coachBargeRef.current;
              if (!cur || Date.now() >= cur.expiry) break;
              await new Promise((r) => setTimeout(r, 50)); // up to ~5s for the join response
              expected = coachBargeRef.current?.expectedSid || null;
            }
            if (expected && incomingSid && incomingSid === expected) {
              coachBargeRef.current = null; // one-shot
              try {
                const ok = await requestMicrophonePermission();
                if (!ok) { call.reject(); window.dispatchEvent(new CustomEvent('coach:barge-ended')); return; }
                coachBargeCallRef.current = call;
                // When this leg ends (call ended, supervisor hung up, conference
                // died) signal LiveCalls to clear the barge UI.
                call.on('disconnect', () => {
                  if (coachBargeCallRef.current === call) coachBargeCallRef.current = null;
                  try { window.dispatchEvent(new CustomEvent('coach:barge-ended')); } catch {}
                });
                call.accept();
              } catch {
                try { call.reject(); } catch {}
                try { window.dispatchEvent(new CustomEvent('coach:barge-ended')); } catch {}
              }
              return; // handled — skip normal incoming-call routing
            }
            // Not our coach leg (or couldn't confirm) — fall through to normal handling.
          }
        }

        // Check if agent is busy — active call or in a dialer session
        const currentActiveCall = activeCallRef.current;
        const currentActiveCallSid = activeCallSidRef.current;
        // Also check dialer session modes — between calls the agent disconnects (activeCall=null)
        // but the session is still active. Without this check, an incoming call during that
        // window would be accepted and disrupt the dialer session.
        const inDialerSession = isInMultiLineModeRef.current || isInPowerDialerModeRef.current;
        // Also check the active_call sessionStorage flag — covers the edge case where
        // an incoming call arrives during multi-line batch creation (before isInMultiLineMode is set)
        // or during manual call setup (before activeCall is set)
        let hasActiveCallFlag = false;
        try { hasActiveCallFlag = sessionStorage.getItem('insuracentral_active_call') === 'true'; } catch {}

        // LIVE TRANSFER interception (2026-07-23, consolidated): identify a
        // pending transfer leg BEFORE the generic busy-reject — a PAUSED
        // session agent (no live call) must get the offer card (transfer-BACK
        // depends on it), while a truly-on-a-call user auto-declines instantly.
        {
          let offer = transferOfferRef.current;
          if (offer && Date.now() < offer.expiry) {
            const incomingSid = (call.parameters as any)?.CallSid as string | undefined;
            for (let i = 0; offer && offer.participantCallSid === 'pending' && i < 100; i++) {
              if (Date.now() >= offer.expiry) break;
              await new Promise((r) => setTimeout(r, 50));
              offer = transferOfferRef.current;
            }
            if (offer && incomingSid && incomingSid === offer.participantCallSid) {
              const onLiveCall = !!(activeCallRef.current || activeCallSidRef.current);
              if (onLiveCall) {
                // True busy → instant decline (agent keeps their info fresh).
                transferOfferRef.current = null;
                try { call.reject(); } catch {}
                api.post({
                  url: getApiUrl('/communication/transfer/resolve'),
                  body: {
                    action: 'declined', callSid: offer.callSid,
                    conferenceSid: offer.conferenceSid, participantCallSid: offer.participantCallSid,
                    phoneParticipantCallSid: (offer as any).phoneParticipantCallSid,
                    isBack: (offer as any).isBack,
                  },
                  auth: true,
                }).catch(() => {});
                return;
              }
              // Idle OR paused-in-session → show the offer card.
              transferOfferRef.current = null; // one-shot
              transferCallRef.current = call;
              setIncomingTransferOffer({
                agentName: offer.agentName, leadName: offer.leadName, leadId: offer.leadId,
                customerNumber: offer.customerNumber, callSid: offer.callSid,
                conferenceSid: offer.conferenceSid, participantCallSid: offer.participantCallSid,
                phoneParticipantCallSid: (offer as any).phoneParticipantCallSid,
                isBack: (offer as any).isBack,
                dropUserId: (offer as any).dropUserId,
              });
              call.on('cancel', () => { if (transferCallRef.current === call) { transferCallRef.current = null; setIncomingTransferOffer(null); } });
              call.on('disconnect', () => { if (transferCallRef.current === call) { transferCallRef.current = null; setIncomingTransferOffer(null); } });
              return; // handled — the offer card drives accept/decline
            }
          }
        }

        if (currentActiveCall || currentActiveCallSid || inDialerSession || hasActiveCallFlag) {
          // Agent is busy (active call or dialer session). Reject this SDK
          // offer so Twilio doesn't auto-join the incoming's conference and
          // disrupt the active call. The user still sees the call via the
          // Supabase-driven IncomingCallWidget (Hold & Answer / Merge /
          // Decline) — a NEW SDK call is created when they click Answer,
          // so rejecting this one is harmless.
          //
          // NO "Missed Call" toast — the widget is the truth-of-record for
          // what the user sees. Showing a missed-call toast while the widget
          // is still ringing (and the user is about to click Hold/Merge)
          // creates contradictory UX. Real missed calls are captured in
          // call_histories and surfaced through the notification center.
          call.reject();
          return; // Don't proceed with normal incoming call handling
        }

        // No active call - proceed normally
        setActiveCall(call);
        setCallStatus("incoming");

        // Request microphone permission for incoming call
        const hasPermission = await requestMicrophonePermission();
        if (!hasPermission) {

          call.reject();
          return;
        }

        setupCallListeners(call);
      });

      twilioDevice.on("unregistered", () => {
        setIsDeviceReady(false);
        // Auto-reconnect when device loses registration (common on iPad/iOS)
        attemptAutoReconnect();
      });

      twilioDevice.on("tokenWillExpire", async () => {
        try {
          const tokenData = await fetchAccessToken();
          if (tokenData) {
            if (carrierChanged(twilioDevice, tokenData)) return;
            await twilioDevice.updateToken(tokenData.token);
            // Schedule next refresh
            scheduleTokenRefresh(tokenData.expiresAt);
          } else {
            // Token fetch returned null — trigger reconnect instead of just showing toast
            console.warn('[TwilioDevice] tokenWillExpire: token fetch returned null, triggering reconnect');
            attemptAutoReconnect();
          }
        } catch (error) {
          // Token refresh failed — trigger reconnect so device doesn't die silently
          console.warn('[TwilioDevice] tokenWillExpire: refresh failed, triggering reconnect');
          attemptAutoReconnect();
        }
      });

      // Register the device
      await twilioDevice.register();
      if (unmountedRef.current) {
        cleanupDevice(twilioDevice); // unmounted mid-registration: don't leave it logged in
        return;
      }
      setDevice(twilioDevice);
      // Latch: from here on, failures are real and always get reported.
      deviceEverUpRef.current = true;
    } catch (error: any) {
      // deviceError state is still set either way — the UI can show an inline
      // "Reconnect" affordance; only the interrupting toast is scoped.
      setDeviceError(error.message || "Failed to initialize device");
      setIsDeviceRegistering(false);
      if (inStartupRecoveryWindow()) {
        console.warn('[TwilioDevice] init failed during startup; recovery will retry:', error?.message);
      } else {
        toast({
          title: "Initialization Failed",
          description: error.message || "Failed to initialize calling device",
          variant: "destructive",
        });
      }
    } finally {
      if (initInFlightRef.current?.run === run) initInFlightRef.current = null;
      finishRun();
    }
  }, [device, toast, scheduleTokenRefresh, inStartupRecoveryWindow]);

  // Lets the reconnect loop (declared earlier) build a device when none exists.
  useEffect(() => {
    initializeDeviceRef.current = initializeDevice;
  }, [initializeDevice]);

  // Start audio level monitoring to detect hardware mute (headset button)
  const startAudioLevelMonitoring = useCallback((call: Call) => {
    // Clean up any existing monitoring
    stopAudioLevelMonitoring();

    // Wait a moment for the call to establish (store timeout ID so it can be cancelled)
    audioMonitorTimeoutRef.current = setTimeout(() => {
      const localStream = call.getLocalStream();
      if (!localStream) {
        console.warn('[TwilioDevice] No local stream for audio monitoring');
        return;
      }

      try {
        // Create audio context and analyser for mic monitoring (separate from speaker audio context)
        const audioContext = new (window.AudioContext || (window as any).webkitAudioContext)();
        const analyser = audioContext.createAnalyser();
        analyser.fftSize = 256;
        analyser.smoothingTimeConstant = 0.3;

        const source = audioContext.createMediaStreamSource(localStream);
        source.connect(analyser);

        micAudioContextRef.current = audioContext;
        analyserRef.current = analyser;
        lowAudioCountRef.current = 0;

        // Monitor audio levels every 1000ms (powers mic level meter in AudioDeviceSelector)
        audioLevelIntervalRef.current = setInterval(() => {
          if (!analyserRef.current) return;

          // Resume mic audio context if iOS suspended it in background
          if (micAudioContextRef.current?.state === 'suspended') {
            micAudioContextRef.current.resume().catch(() => {});
          }

          const dataArray = new Uint8Array(analyserRef.current.frequencyBinCount);
          analyserRef.current.getByteFrequencyData(dataArray);

          // Calculate average audio level (0-255)
          const average = dataArray.reduce((sum, val) => sum + val, 0) / dataArray.length;
          // Convert to 0-100 scale
          const level = Math.round((average / 255) * 100);

          setMicrophoneAudioLevel(level);

          // Hardware mute detection disabled - was causing false positives
          // The Twilio local stream audio levels don't reliably reflect actual mic input
          // TODO: Revisit with a different detection approach if needed
        }, 1000);
      } catch (error) {
        console.error('[TwilioDevice] Failed to start audio monitoring:', error);
      }
    }, 3000); // Wait 3 seconds for call to fully establish before monitoring
  }, [isHardwareMuted]);

  // Stop audio level monitoring (only closes mic audio context, not speaker)
  const stopAudioLevelMonitoring = useCallback(() => {
    if (audioMonitorTimeoutRef.current) {
      clearTimeout(audioMonitorTimeoutRef.current);
      audioMonitorTimeoutRef.current = null;
    }
    if (audioLevelIntervalRef.current) {
      clearInterval(audioLevelIntervalRef.current);
      audioLevelIntervalRef.current = null;
    }
    if (micAudioContextRef.current) {
      micAudioContextRef.current.close().catch(() => {});
      micAudioContextRef.current = null;
    }
    analyserRef.current = null;
    lowAudioCountRef.current = 0;
    setMicrophoneAudioLevel(0);
    setIsHardwareMuted(false);
  }, []);

  // Public wrappers — start/stop mic level monitoring on demand (AudioDeviceSelector open/close)
  const startAudioMonitoring = useCallback(() => {
    if (activeCallRef.current) {
      startAudioLevelMonitoring(activeCallRef.current);
    }
  }, [startAudioLevelMonitoring]);

  const stopAudioMonitoring = useCallback(() => {
    stopAudioLevelMonitoring();
  }, [stopAudioLevelMonitoring]);

  // Refresh available audio devices
  const refreshAudioDevices = useCallback(async () => {
    try {
      // Need to request permission first to get device labels
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      // Immediately stop all tracks to release the microphone — we only needed permission for labels
      stream.getTracks().forEach(track => track.stop());

      const devices = await navigator.mediaDevices.enumerateDevices();

      const inputDevices: AudioDevice[] = devices
        .filter(d => d.kind === 'audioinput')
        .map(d => ({
          deviceId: d.deviceId,
          label: d.label || `Microphone ${d.deviceId.slice(0, 5)}`,
          kind: 'audioinput' as const,
        }));

      const outputDevices: AudioDevice[] = devices
        .filter(d => d.kind === 'audiooutput')
        .map(d => ({
          deviceId: d.deviceId,
          label: d.label || `Speaker ${d.deviceId.slice(0, 5)}`,
          kind: 'audiooutput' as const,
        }));

      setAudioInputDevices(inputDevices);
      setAudioOutputDevices(outputDevices);

      // If no device selected yet, select the first one (default)
      if (!selectedInputDeviceId && inputDevices.length > 0) {
        setSelectedInputDeviceId(inputDevices[0].deviceId);
      }
      if (!selectedOutputDeviceId && outputDevices.length > 0) {
        setSelectedOutputDeviceId(outputDevices[0].deviceId);
      }
    } catch (error) {
      console.error('[TwilioDevice] Failed to refresh audio devices:', error);
    }
  }, [selectedInputDeviceId, selectedOutputDeviceId]);

  // Set audio input device (microphone)
  const setAudioInputDevice = useCallback(async (deviceId: string) => {
    try {
      if (device) {
        // Twilio SDK method to set input device
        await device.audio?.setInputDevice(deviceId);
        setSelectedInputDeviceId(deviceId);

        const deviceLabel = audioInputDevices.find(d => d.deviceId === deviceId)?.label || deviceId;

        toast({
          title: "Microphone Changed",
          description: `Now using: ${deviceLabel}`,
        });
      }
    } catch (error) {
      console.error('[TwilioDevice] Failed to set audio input device:', error);
      toast({
        title: "Failed to Change Microphone",
        description: "Could not switch to the selected microphone",
        variant: "destructive",
      });
    }
  }, [device, audioInputDevices, toast]);

  // Set audio output device (speaker)
  // Safari fix: Safari requires a user gesture for setSinkId() which Twilio calls internally.
  // On Safari, we skip speakerDevices.set() to avoid "NotAllowedError: A user gesture is required".
  // Audio still works — it just uses the system default output device.
  const setAudioOutputDevice = useCallback(async (deviceId: string) => {
    try {
      if (device) {
        if (isSafariBrowser) {
          // Safari doesn't fully support setSinkId — just track the selection in state
          // Audio will route through the system default output device
          setSelectedOutputDeviceId(deviceId);
          const deviceLabel = audioOutputDevices.find(d => d.deviceId === deviceId)?.label || deviceId;
          toast({
            title: "Speaker Selected",
            description: `${deviceLabel} (Safari uses system audio output)`,
          });
          return;
        }

        // Twilio SDK method to set output device (Chrome/Edge/Firefox)
        await device.audio?.speakerDevices.set(deviceId);
        setSelectedOutputDeviceId(deviceId);

        const deviceLabel = audioOutputDevices.find(d => d.deviceId === deviceId)?.label || deviceId;

        toast({
          title: "Speaker Changed",
          description: `Now using: ${deviceLabel}`,
        });
      }
    } catch (error: any) {
      // Handle setSinkId NotAllowedError gracefully (can happen on Safari or strict browsers)
      if (error?.name === 'NotAllowedError') {
        console.warn('[TwilioDevice] setSinkId blocked by browser — using default audio output');
        setSelectedOutputDeviceId(deviceId);
        toast({
          title: "Speaker Selected",
          description: "Your browser uses the system audio output setting",
        });
        return;
      }
      console.error('[TwilioDevice] Failed to set audio output device:', error);
      toast({
        title: "Failed to Change Speaker",
        description: "Could not switch to the selected speaker",
        variant: "destructive",
      });
    }
  }, [device, audioOutputDevices, toast]);

  // Set microphone gain (0-200, 100 = normal)
  // NOTE: Twilio Voice SDK doesn't expose direct gain control on the outgoing audio stream.
  // The microphone gain slider primarily affects the visual level indicator and serves as
  // a UI affordance. For actual gain changes, users should adjust their system mic settings.
  // We store the value for potential future WebRTC track manipulation.
  const setMicrophoneGain = useCallback((gain: number) => {
    const clampedGain = Math.max(0, Math.min(200, gain));
    setMicrophoneGainState(clampedGain);

    // If we have a gain node connected, update it
    if (micGainNodeRef.current) {
      // Convert 0-200 to 0-2 multiplier
      micGainNodeRef.current.gain.value = clampedGain / 100;
    }
  }, []);

  // Set speaker/output volume (0-200, 100 = normal)
  // Uses Web Audio API GainNode for amplification beyond 100%
  // For 0-100%, we use the HTML audio element volume directly
  const setSpeakerVolume = useCallback((volume: number) => {
    const clampedVolume = Math.max(0, Math.min(200, volume));
    setSpeakerVolumeState(clampedVolume);

    // Resume audio context if iOS suspended it in background
    if (speakerAudioContextRef.current?.state === 'suspended') {
      speakerAudioContextRef.current.resume().catch(() => {});
    }

    // Apply volume via Web Audio API GainNode if available (supports 0-200%)
    if (speakerGainNodeRef.current) {
      // Convert 0-200 to 0-2 multiplier
      speakerGainNodeRef.current.gain.value = clampedVolume / 100;
    }

    // Also set the Twilio remote audio element volume as fallback/alternative
    if (twilioRemoteAudioRef.current) {
      // HTML audio volume is 0-1, so cap at 1 for values > 100
      // The gain node handles amplification beyond 100%
      const htmlVolume = Math.min(1, clampedVolume / 100);
      twilioRemoteAudioRef.current.volume = htmlVolume;
      console.log('[TwilioDevice] Applied audio element volume:', htmlVolume);
    }
  }, []);

  // Setup Web Audio API for speaker volume control with amplification support
  // This intercepts Twilio's remote audio element and routes through a gain node
  const setupSpeakerAudioPipeline = useCallback((audioElement: HTMLAudioElement) => {
    // Store reference to Twilio's audio element
    twilioRemoteAudioRef.current = audioElement;

    // Detect iOS/iPadOS - these have issues with Web Audio API + createMediaElementSource
    // Safari and Chrome on iOS share the same WebKit engine with these limitations
    const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent) ||
      (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);

    if (isIOS) {
      // On iOS/iPad: Skip Web Audio API pipeline, use simple volume control (0-100% only)
      // This prevents audio glitches and call issues on these devices
      console.log('[TwilioDevice] iOS detected - using simple volume control (no amplification)');
      audioElement.volume = Math.min(1, speakerVolume / 100);
      return;
    }

    try {
      console.log('[TwilioDevice] Setting up speaker audio pipeline from audio element');

      // Clean up existing speaker audio nodes
      if (speakerSourceNodeRef.current) {
        speakerSourceNodeRef.current.disconnect();
        speakerSourceNodeRef.current = null;
      }
      if (speakerGainNodeRef.current) {
        speakerGainNodeRef.current.disconnect();
        speakerGainNodeRef.current = null;
      }

      // Create or reuse AudioContext for speaker (separate from mic monitoring)
      let audioContext = speakerAudioContextRef.current;
      if (!audioContext || audioContext.state === 'closed') {
        audioContext = new AudioContext();
        speakerAudioContextRef.current = audioContext;
      }

      // Resume audio context if suspended (browser autoplay policy)
      if (audioContext.state === 'suspended') {
        audioContext.resume();
      }

      // Create source from the audio element (captures Twilio's remote audio)
      const source = audioContext.createMediaElementSource(audioElement);
      speakerSourceNodeRef.current = source as unknown as MediaStreamAudioSourceNode;

      // Create gain node for volume control (0-200% = 0-2x)
      const gainNode = audioContext.createGain();
      speakerGainNodeRef.current = gainNode;

      // Set initial volume (0-200 maps to 0-2)
      gainNode.gain.value = speakerVolume / 100;

      // Connect: source -> gain -> destination (speakers)
      source.connect(gainNode);
      gainNode.connect(audioContext.destination);

      console.log('[TwilioDevice] Speaker audio pipeline setup with gain control, initial volume:', speakerVolume);
    } catch (error) {
      console.error('[TwilioDevice] Failed to setup speaker audio pipeline:', error);
      // Fallback: just control the audio element volume directly (limited to 0-100%)
      audioElement.volume = Math.min(1, speakerVolume / 100);
    }
  }, [speakerVolume]);

  // Cleanup speaker audio pipeline (called on call disconnect)
  const cleanupSpeakerAudioPipeline = useCallback(() => {
    if (speakerSourceNodeRef.current) {
      speakerSourceNodeRef.current.disconnect();
      speakerSourceNodeRef.current = null;
    }
    if (speakerGainNodeRef.current) {
      speakerGainNodeRef.current.disconnect();
      speakerGainNodeRef.current = null;
    }
    // Close the speaker audio context (now separate from mic context)
    if (speakerAudioContextRef.current) {
      speakerAudioContextRef.current.close().catch(() => {});
      speakerAudioContextRef.current = null;
    }
    // Clear the Twilio audio element reference
    twilioRemoteAudioRef.current = null;
  }, []);

  // 3-Way Calling Functions
  const refreshConferenceParticipants = useCallback(async () => {
    const conferenceName = currentConferenceName || (activeCallSid ? `call-${activeCallSid}` : null);
    if (!conferenceName) return;
    try {
      const response = await callService.getConferenceParticipants(conferenceName);
      if (response.success) {
        // Filter the Add Party list to only show OTHER parties — not the
        // agent (who controls themselves via the main widget) and not the
        // main active customer (already shown in the Active Call header).
        // Match by last-10-digits so +1/E.164/national format differences
        // between callMetadata (what our code stores) and the Twilio Call
        // from/to field don't cause false misses.
        const last10 = (phone: string): string =>
          (phone || '').replace(/\D/g, '').slice(-10);
        const mainPhoneLast10 = last10(callMetadata?.phoneNumber || '');
        const filtered = (response.participants || []).filter((p: any) => {
          if (p.role === 'agent') return false;
          if (mainPhoneLast10 && last10(p.phoneNumber) === mainPhoneLast10) return false;
          return true;
        });
        setConferenceParticipants(filtered);
      }
    } catch (error) {
      console.error('Failed to refresh conference participants:', error);
    }
  }, [currentConferenceName, activeCallSid, callMetadata?.phoneNumber]);

  const addParticipantToCall = useCallback(async (
    phoneNumber: string,
    name?: string,
    role?: 'agent' | 'customer' | 'consultant'
  ): Promise<boolean> => {
    // Use currentConferenceName if set, otherwise derive from activeCallSid
    // The conference friendly name format is "call-{parentCallSid}" for outbound calls
    // or "incoming-{callSid}" for incoming calls
    const conferenceName = currentConferenceName || (activeCallSid ? `call-${activeCallSid}` : null);

    if (!conferenceName) {
      console.error('No active conference or call to add participant to');
      return false;
    }

    setIsAddingParticipant(true);
    try {
      const response = await callService.addParticipantToCall({
        conferenceName,
        phoneNumber,
        name,
        role,
      });

      if (response.success && response.participant) {
        setConferenceParticipants(prev => [...prev, response.participant!]);

        // Play ringback sound while waiting for the participant to answer
        // The sound will stop when we receive the participant_connected event
        playRingback();

        return true;
      }
      return false;
    } catch (error) {
      console.error('Failed to add participant:', error);
      return false;
    } finally {
      setIsAddingParticipant(false);
    }
  }, [currentConferenceName, activeCallSid]);

  const removeParticipantFromCall = useCallback(async (participantSid: string): Promise<boolean> => {
    const conferenceName = currentConferenceName || (activeCallSid ? `call-${activeCallSid}` : null);
    if (!conferenceName) return false;

    try {
      const response = await callService.controlParticipant({
        conferenceName,
        participantSid,
        action: 'remove',
      });

      if (response.success) {
        setConferenceParticipants(prev =>
          prev.filter(p => p.participantSid !== participantSid)
        );
        return true;
      }
      return false;
    } catch (error) {
      console.error('Failed to remove participant:', error);
      return false;
    }
  }, [currentConferenceName, activeCallSid]);

  const muteParticipant = useCallback(async (participantSid: string, mute: boolean): Promise<boolean> => {
    const conferenceName = currentConferenceName || (activeCallSid ? `call-${activeCallSid}` : null);
    if (!conferenceName) return false;

    try {
      const response = await callService.controlParticipant({
        conferenceName,
        participantSid,
        action: mute ? 'mute' : 'unmute',
      });

      if (response.success) {
        setConferenceParticipants(prev =>
          prev.map(p => p.participantSid === participantSid
            ? { ...p, isMuted: mute }
            : p
          )
        );
        return true;
      } else if (response.error?.includes('no longer in the conference')) {
        // Participant has disconnected, remove them from the list
        setConferenceParticipants(prev =>
          prev.filter(p => p.participantSid !== participantSid && p.callSid !== participantSid)
        );
      }
      return false;
    } catch (error) {
      console.error('Failed to mute/unmute participant:', error);
      // If the error indicates participant is gone, remove them from the list
      const errorMessage = error instanceof Error ? error.message : String(error);
      if (errorMessage.includes('no longer in the conference') || errorMessage.includes('disconnected')) {
        setConferenceParticipants(prev =>
          prev.filter(p => p.participantSid !== participantSid && p.callSid !== participantSid)
        );
      }
      return false;
    }
  }, [currentConferenceName, activeCallSid]);

  const holdParticipant = useCallback(async (participantSid: string, hold: boolean): Promise<boolean> => {
    const conferenceName = currentConferenceName || (activeCallSid ? `call-${activeCallSid}` : null);
    if (!conferenceName) return false;

    try {
      const response = await callService.controlParticipant({
        conferenceName,
        participantSid,
        action: hold ? 'hold' : 'unhold',
      });

      if (response.success) {
        setConferenceParticipants(prev =>
          prev.map(p => p.participantSid === participantSid
            ? { ...p, isOnHold: hold, status: hold ? 'hold' : 'connected' }
            : p
          )
        );
        return true;
      } else if (response.error?.includes('no longer in the conference')) {
        // Participant has disconnected, remove them from the list
        setConferenceParticipants(prev =>
          prev.filter(p => p.participantSid !== participantSid && p.callSid !== participantSid)
        );
      }
      return false;
    } catch (error) {
      console.error('Failed to hold/unhold participant:', error);
      // If the error indicates participant is gone, remove them from the list
      const errorMessage = error instanceof Error ? error.message : String(error);
      if (errorMessage.includes('no longer in the conference') || errorMessage.includes('disconnected')) {
        setConferenceParticipants(prev =>
          prev.filter(p => p.participantSid !== participantSid && p.callSid !== participantSid)
        );
      }
      return false;
    }
  }, [currentConferenceName, activeCallSid]);

  // Add a function to set the conference name when a call becomes a conference
  const setActiveConference = useCallback((conferenceName: string | null) => {
    setCurrentConferenceName(conferenceName);
    if (!conferenceName) {
      setConferenceParticipants([]);
    }
  }, []);

  // Setup call listeners
  // sessionInfo is optional - when provided, the call session will be persisted for page refresh recovery
  const setupCallListeners = (call: Call, sessionInfo?: {
    leadId?: string;
    leadName?: string;
    phoneNumber?: string;
    source?: string;
    mode?: 'power_dialer' | 'manual' | 'incoming';
    isRecovery?: boolean; // Don't re-persist if this is already a recovery
  }) => {
    // Determine if this is an incoming call BEFORE the accept handler fires.
    // We can't use call.direction because conference-based outbound calls also show as INCOMING.
    // Instead, check: if mode is 'incoming', or if the current callStatus is "incoming" (set by device.on("incoming")).
    const isIncomingCall = sessionInfo?.mode === 'incoming' || callStatus === 'incoming';

    call.on("accept", () => {
      // NOTE: Do NOT stop ringback here - 'accept' fires when the AGENT joins the conference,
      // NOT when the customer answers. The ringback should keep playing until we receive
      // the 'customer_answered' Supabase realtime event.
      // stopRingback() is now called in the customer_answered listener below.

      // Request Wake Lock to prevent screen sleep during call (iOS/iPad fix)
      requestWakeLock();

      // Reset reconnect counter on successful call — ensures only consecutive failures count
      reconnectAttemptRef.current = 0;

      // For OUTBOUND calls: set "ringing" because the customer hasn't answered yet.
      // Status will change to "in-progress" when customer_answered event fires.
      // For INCOMING calls: set "in-progress" immediately because the customer is already on the line.
      // TRANSFER ACCEPT (2026-07-23): the owner's accepted transfer leg is
      // neither — the customer is LIVE in the conference. The stale-closure
      // isIncomingCall saw 'ringing' → badge stuck "Ringing…". The accept path
      // marks the call object directly.
      setCallStatus(isIncomingCall || (call as any).__transferAccepted ? "in-progress" : "ringing");
      setCallDuration(0);

      // Android: start the active-call foreground service so screen-lock /
      // backgrounding can't freeze the call's audio (no-op elsewhere). Runs
      // for BOTH directions — Android has no native inbound registration.
      import("@/lib/capacitor/call-foreground")
        .then((fgs) => fgs.startCallForegroundService(callMetadataRef.current?.leadName || undefined))
        .catch(() => {});

      // CallKit: register OUTBOUND calls with iOS. Without a reported call,
      // locking the phone suspends the webview's audio and kills the call —
      // the bridge had reportCallStarted/Ended fully built but NO caller.
      // Inbound CallKit calls are already reported natively on the VoIP push.
      if (!isIncomingCall && !(call as any).__ckUuid) {
        import("@/lib/capacitor/callkit")
          .then(async (ck) => {
            if (!ck.isCallKitAvailable()) return;
            const uuid = await ck.reportOutgoingCallStarted(
              (call.parameters as any)?.To || "Outbound Call",
              call.parameters?.CallSid || undefined,
            );
            if (uuid) {
              (call as any).__ckUuid = uuid;
              await ck.reportCallConnected(uuid);
            }
          })
          .catch(() => {});
      }

      // Update the CallSid when it becomes available after Twilio accepts the call
      // CallSid is undefined initially when device.connect() returns, but gets populated after accept
      const callSid = call.parameters.CallSid || null;
      setActiveCallSid(callSid);

      // CRITICAL FIX: Update the ref IMMEDIATELY, not just via useEffect
      // This ensures customer_answered events can match even before React re-renders
      // Without this, there's a race condition where the event arrives before the useEffect runs,
      // causing the ringback to keep playing even after the customer answers
      if (callSid) {
        activeCallSidRef.current = callSid;

        // Persist call session for page refresh recovery (unless this is already a recovery)
        // For outbound calls, the conference name is "call-{callSid}"
        // For multi-line dialer, this is handled separately (already has its own persistence)
        if (sessionInfo && !sessionInfo.isRecovery) {
          const conferenceName = currentConferenceName || `call-${callSid}`;
          persistCallSession({
            callSid,
            conferenceName,
            leadId: sessionInfo.leadId,
            leadName: sessionInfo.leadName,
            phoneNumber: sessionInfo.phoneNumber || '',
            startTime: Date.now(),
            mode: sessionInfo.mode || 'manual',
            source: sessionInfo.source,
          });
        }
      }

      // Start duration timer
      if (durationIntervalRef.current) {
        clearInterval(durationIntervalRef.current);
      }
      durationIntervalRef.current = setInterval(() => {
        setCallDuration((prev) => prev + 1);
      }, 1000);

      // Ringback is stopped via Supabase realtime customer_answered event

      // CRITICAL: Verify local audio (agent's microphone) is transmitting
      // Add a small delay to ensure WebRTC connection is fully established
      setTimeout(() => {
        const localStream = call.getLocalStream();
        if (localStream) {
          const audioTracks = localStream.getAudioTracks();
          if (audioTracks.length > 0) {
            const audioTrack = audioTracks[0];

            // Ensure the audio track is enabled (not muted at source)
            if (!audioTrack.enabled) {
              console.warn('[TwilioDevice] Local audio track was disabled, enabling...');
              audioTrack.enabled = true;
            }

            // NOTE: We no longer auto-unmute here - if user intentionally muted before customer answered,
            // we should respect that. The isMuted state tracks user intent.

            // Check RTCPeerConnection state
            const peerConnection = (call as any)._peerConnection || (call as any).peerConnection;
            if (peerConnection && peerConnection.connectionState !== 'closed') {
              // Check if audio is being sent
              try {
                peerConnection.getSenders().forEach((sender: RTCRtpSender) => {
                  if (sender.track?.kind === 'audio' && !sender.track.enabled) {
                    sender.track.enabled = true;
                  }
                });
              } catch {
                // Peer connection may have closed between check and access
              }
            }
          } else {
            console.error('[TwilioDevice] No local audio tracks found - agent microphone may not be working');
          }
        } else {
          console.error('[TwilioDevice] No local stream found - agent microphone may not be connected');
        }
      }, 500); // 500ms delay to ensure WebRTC is established

      // Also check at 2 seconds to catch any delayed issues with audio tracks
      // NOTE: We no longer auto-unmute here - if user intentionally muted before customer answered,
      // we should respect that. We only ensure audio tracks are enabled at the source level.
      setTimeout(() => {
        // Skip if call already disconnected
        if (call.status() === 'closed') return;
        try {
          const localStream = call.getLocalStream();
          if (localStream) {
            const audioTracks = localStream.getAudioTracks();
            audioTracks.forEach((track, i) => {
              if (!track.enabled) {
                console.warn(`[TwilioDevice] Track ${i} disabled at 2s check, enabling...`);
                track.enabled = true;
              }
            });
          }
        } catch {
          // Call may have been cleaned up
        }
      }, 2000);

    });

    // Listen for Twilio's remote audio element to set up volume control
    // This event fires when Twilio creates the HTMLAudioElement for the remote caller's audio
    call.on("audio", (audioElement: HTMLAudioElement) => {
      console.log('[TwilioDevice] Received audio event with audio element');
      setupSpeakerAudioPipeline(audioElement);
      // Settings → "Soften loud calls" (no-op while off); every dialer's calls come through here.
      call.on("disconnect", softenCallAudio(audioElement));
    });

    // BULLETPROOF RINGBACK KILL: fallback for Supabase realtime.
    //
    // Primary path: backend broadcasts customer_answered → IncomingCallContext
    // listener calls stopRingback(). That's what ALL dialers rely on.
    //
    // When Supabase realtime drops the packet (socket blip, backgrounded tab,
    // Render shared-tier noise), ringback runs until the 30s hard timeout.
    // Mark hit this on a manual dial: heard ringing for ~18s while already
    // talking to Kathy.
    //
    // Local fallback: Twilio SDK fires `volume` events (~50ms cadence) with
    // inputVolume (agent mic) and outputVolume (what agent hears). In the
    // conference-based flow, agent joins a silent conference; outputVolume
    // stays ~0 until the customer's leg actually joins. Once we see several
    // consecutive non-silent output samples, the customer is connected →
    // stop ringback locally. No realtime dependency.
    //
    // Applies to ALL outbound dialers (manual, power, multi-line). Matches
    // multi-line's polling-based safety net but works 100% in-browser.
    let nonSilentSamples = 0;
    const SILENCE_THRESHOLD = 0.01; // Twilio volumes are 0..1
    const REQUIRED_CONSECUTIVE = 3; // ~150ms of non-silent audio
    const volumeHandler = (_input: number, outputVolume: number) => {
      if (outputVolume > SILENCE_THRESHOLD) {
        nonSilentSamples++;
        if (nonSilentSamples >= REQUIRED_CONSECUTIVE) {
          stopRingback();
          call.off("volume", volumeHandler);
        }
      } else {
        nonSilentSamples = 0;
      }
    };
    call.on("volume", volumeHandler);

    call.on("disconnect", async () => {
      // Tell usage-aware UI (trial talk-time pill, usage bars) a call just
      // ended so they refetch instead of waiting for their next poll.
      try { window.dispatchEvent(new CustomEvent("ic-call-ended")); } catch { /* ignore */ }

      // Android: drop the active-call foreground service (no-op elsewhere).
      import("@/lib/capacitor/call-foreground")
        .then((fgs) => fgs.stopCallForegroundService())
        .catch(() => {});

      // Review prompt at the BEST moment a dialer has: just finished a real
      // conversation (2+ min). Heavily self-throttled — see app-review.ts.
      if (callDurationRef.current >= 120) {
        import("@/lib/capacitor/app-review")
          .then((r) => r.maybeRequestReview("call-completed", 0.35))
          .catch(() => {});
      }

      // CallKit: close the system call registered in the accept handler, or
      // iOS keeps showing an active call after hangup.
      const ckUuid = (call as any).__ckUuid;
      if (ckUuid) {
        (call as any).__ckUuid = null;
        import("@/lib/capacitor/callkit")
          .then((ck) => ck.reportCallEnded(ckUuid))
          .catch(() => {});
      }

      // Announce the call end so open views (lead Recent Activity timeline etc.)
      // can refetch immediately instead of waiting for a poll / page refresh.
      // Fired in ALL modes (manual/power/multi-line agent leg).
      try {
        window.dispatchEvent(new CustomEvent('dialer:call-ended', {
          detail: { leadId: callMetadataRef.current?.leadId || null },
        }));
      } catch { /* non-critical */ }

      // Hangup attribution toast — tells the agent who ended the call AND who it was.
      // Skipped in multi-line mode (the dialer fires its own toasts only when an
      // actual connected call ends — see MultiLineDialerSession). The agent's SDK
      // leg here disconnects on every batch boundary, so toasting it is noise.
      if (!isInMultiLineModeRef.current) {
        const meta = callMetadataRef.current;
        const dur = callDurationRef.current;
        // Display name: lead name if known, formatted phone otherwise, "Caller" as last resort.
        const displayName = meta?.leadName?.trim()
          || (meta?.phoneNumber ? formatPhoneForDisplay(meta.phoneNumber) : null)
          || 'Caller';
        const durLabel = dur > 0 ? formatDurationLabel(dur) : null;
        const wasUserHangup = hangupAttributionRef.current === 'user';

        if (wasUserHangup && dur === 0) {
          // Agent cancelled an outbound while it was still ringing (never connected).
          toast({
            title: `Cancelled call to ${displayName}`,
            description: 'You hung up before they answered.',
            duration: 4000,
            className: HANGUP_TOAST_CLASS.agentCancelled,
          });
        } else if (wasUserHangup) {
          // Agent ended a connected call (any duration).
          toast({
            title: `You ended the call with ${displayName}`,
            description: `Talk time: ${durLabel}.`,
            duration: 4000,
            className: HANGUP_TOAST_CLASS.agentEndedConnected,
          });
        } else if (dur === 0) {
          // Outbound never connected. Twilio's status code (no-answer / busy / failed /
          // canceled) is on the call.parameters.CallStatus side but not always reliable
          // in the SDK — so we just say it didn't connect, no assumption about why.
          toast({
            title: `${displayName} didn't pick up`,
            description: 'Call ended before they connected.',
            variant: "destructive",
            duration: 4500,
            className: HANGUP_TOAST_CLASS.needsCallback,
          });
        } else if (dur < 4) {
          // Connected briefly then ended — could be voicemail beep, wrong number,
          // robocall hangup, or just a fast remote hangup. Don't guess; report fact.
          toast({
            title: `${displayName} dropped after ${durLabel}`,
            description: 'They picked up and ended the call right away.',
            variant: "destructive",
            duration: 5000,
            className: HANGUP_TOAST_CLASS.leadHungUp,
          });
        } else {
          // Real conversation — remote party hung up. Agent should know it wasn't them.
          toast({
            title: `${displayName} hung up`,
            description: `They ended the call after ${durLabel}.`,
            variant: "destructive",
            duration: 5000,
            className: HANGUP_TOAST_CLASS.leadHungUp,
          });
        }
      }
      hangupAttributionRef.current = null;

      // Release Wake Lock - screen can now sleep (iOS/iPad)
      releaseWakeLock();
      // Stop ringback audio (in case call ended before answer)
      stopRingback();
      // Stop audio level monitoring
      stopAudioLevelMonitoring();
      // Clear persisted call session (page refresh recovery no longer needed)
      clearPersistedCallSession();

      setActiveCall(null);
      setActiveCallSid(null);
      setCallMetadata(null);
      setCallStatus("");
      setIsMuted(false);
      setIsOnHold(false);
      setIsRecording(false);
      setCallDuration(0);
      setForceShowWidget(false);
      setActiveCallSource(null);
      // Clear conference state for 3-way calling
      setCurrentConferenceName(null);
      setConferenceParticipants([]);
      // NOTE: Do NOT clear isInMultiLineMode here — the multi-line dialer session
      // component manages this state. Clearing it on disconnect breaks multi-line mode
      // because the agent disconnects between batches.

      // Clear duration timer
      if (durationIntervalRef.current) {
        clearInterval(durationIntervalRef.current);
        durationIntervalRef.current = null;
      }

      // Clear audio pipeline
      cleanupSpeakerAudioPipeline();
      if (remoteAudioRef.current) {
        remoteAudioRef.current.srcObject = null;
      }

      // Twilio Voice SDK 2.18.5 leaks its volume meters: PeerConnection.close() disconnects the
      // analysers' outputs but never the mic/remote MediaStreamSources feeding them, all in the
      // Device's one AudioContext that is never closed. Multi-line makes a new call every batch, so
      // ~125 such chains per 500 leads keep running on the audio thread ("choppy after ~500 calls,
      // a refresh fixes it", Dom Lulgjuraj 2026-10-09). Unhook them here; private fields, so best effort.
      const mediaHandler = (call as any)._mediaHandler;
      for (const key of ["_inputStreamSource", "_outputStreamSource", "_mediaStreamSource"]) {
        try { mediaHandler?.[key]?.disconnect(); } catch { /* already gone */ }
      }
    });

    call.on("cancel", () => {
      // Incoming caller hung up before the agent accepted. Show a toast so the
      // agent knows who tried to reach them.
      if (!isInMultiLineModeRef.current) {
        const meta = callMetadataRef.current;
        const displayName = meta?.leadName?.trim()
          || (meta?.phoneNumber ? formatPhoneForDisplay(meta.phoneNumber) : null)
          || 'Caller';
        toast({
          title: `${displayName} hung up before you answered`,
          description: 'Missed inbound call.',
          variant: "destructive",
          duration: 5000,
          className: HANGUP_TOAST_CLASS.needsCallback,
        });
      }
      hangupAttributionRef.current = null;

      releaseWakeLock();
      stopRingback();
      // Local fallback for incoming-call ringtone. When a caller hangs up
      // before the agent accepts, Twilio's SDK fires `cancel` on the Call
      // object — no network round-trip, no realtime dependency. If the
      // Supabase `call_ended` broadcast gets dropped, this still stops the
      // ringtone. IncomingCallContext listens for the event.
      if (typeof window !== 'undefined') {
        try { window.dispatchEvent(new Event('twilio:incoming-canceled')); } catch {}
      }
      clearPersistedCallSession(); // Clear any persisted session data
      setActiveCall(null);
      setCallMetadata(null);
      setCallStatus("");
      setForceShowWidget(false);
      setActiveCallSource(null);
      setCurrentConferenceName(null);
      setConferenceParticipants([]);
      setIsInMultiLineMode(false);
    });

    call.on("reject", () => {
      // Agent declined an incoming call — confirm so they don't think it failed silently
      // and remember whose call they declined.
      if (!isInMultiLineModeRef.current) {
        const meta = callMetadataRef.current;
        const displayName = meta?.leadName?.trim()
          || (meta?.phoneNumber ? formatPhoneForDisplay(meta.phoneNumber) : null)
          || 'Caller';
        toast({
          title: `You declined the call from ${displayName}`,
          duration: 2500,
          className: HANGUP_TOAST_CLASS.agentCancelled,
        });
      }
      hangupAttributionRef.current = null;

      releaseWakeLock();
      stopRingback();
      clearPersistedCallSession(); // Clear any persisted session data
      setActiveCall(null);
      setCallMetadata(null);
      setCallStatus("");
      setForceShowWidget(false);
      setActiveCallSource(null);
      setCurrentConferenceName(null);
      setConferenceParticipants([]);
      setIsInMultiLineMode(false);
    });

    call.on("error", (error: any) => {
      stopRingback();

      setCallStatus("error");

      // Enhanced error handling for specific error codes
      let errorTitle = "Call Error";
      let errorDescription = "An error occurred during the call";

      // Check for specific error codes
      const errorCode = error.code || error.twilioError?.code;

      switch (errorCode) {
        case 31603:
          errorTitle = "Invalid Phone Number";
          errorDescription = "The phone number you're trying to call is invalid or cannot be reached. Please verify the number and try again.";
          break;
        case 31600:
          errorTitle = "Call Not Answered";
          errorDescription = "The call was not answered. The recipient may be unavailable or the number may be incorrect.";
          break;
        case 31480:
          errorTitle = "Call Ended";
          errorDescription = "The call has been terminated. This may occur if the recipient hung up or if there was a connection issue.";
          break;
        case 13214:
          errorTitle = "Phone Number Required";
          errorDescription = "You need to purchase an active phone number before making calls. Please go to Settings > Area Codes to purchase a number.";
          break;
        case 20003:
          errorTitle = "Service Unavailable";
          errorDescription = "Dialer service is temporarily unavailable. Please contact support.";
          break;
        case 20101:
          errorTitle = "Session Expired";
          errorDescription = "Your session has expired. Please refresh the page to reconnect.";
          break;
        case 20104:
          errorTitle = "Token Expired";
          errorDescription = "Your dialer access token has expired. Please refresh the page.";
          break;
        default:
          // Don't expose raw error messages - use generic message
          errorDescription = "An error occurred during the call. Please try again.";
          break;
      }

      toast({
        title: errorTitle,
        description: errorDescription,
        variant: "destructive",
      });

      // BUG FIX: Clean up call state after error. Some Twilio errors (31600, 31603, etc.)
      // may NOT fire a subsequent 'disconnect' event, leaving activeCall stuck as non-null.
      // This blocks the power dialer's auto-advance which checks !activeCall.
      // Use a short delay to let Twilio SDK fire disconnect if it's going to — if not,
      // force-cleanup so the dialer doesn't permanently stall.
      setTimeout(() => {
        // Only cleanup if the call is still the same one that errored (not a new call)
        setActiveCall((current) => {
          if (current === call) {
            // Call is still the errored one — force cleanup
            releaseWakeLock();
            stopRingback();
            stopAudioLevelMonitoring();
            clearPersistedCallSession();
            setActiveCallSid(null);
            setCallMetadata(null);
            setCallStatus("");
            setIsMuted(false);
            setIsOnHold(false);
            setIsRecording(false);
            setCallDuration(0);
            setForceShowWidget(false);
            setActiveCallSource(null);
            setCurrentConferenceName(null);
            setConferenceParticipants([]);
            if (durationIntervalRef.current) {
              clearInterval(durationIntervalRef.current);
              durationIntervalRef.current = null;
            }
            return null; // Clear activeCall
          }
          return current; // Different call — don't touch
        });
      }, 2000); // 2s grace period for disconnect event to fire naturally
    });

    call.on("mute", (isMuted, call) => {
      setIsMuted(isMuted);
    });

    // Listen for DTMF tones from the customer (when they press keys on their phone)
    // Note: This event is not directly available in Twilio Client SDK
    // DTMF tones are automatically passed through as audio to the agent's browser
    // For server-side logging, DTMF would need to be captured via Twilio's REST API or webhooks

    // Log call setup for DTMF debugging

  };

  // Connect to a call
  const connect = async (params: { to: string; from?: string; displayFrom?: string; leadId?: string; metadata?: Record<string, any>; forceShowWidget?: boolean }): Promise<Call | null> => {
    // TRANSFER COLLISION (2026-07-23): if a transfer offer is still ringing
    // this device when the owner starts ANY other call (typically answering a
    // real incoming — answerCall funnels through connect()), auto-decline the
    // pending transfer: reject the stashed leg (frees the Device for the new
    // connect — a pending incoming would otherwise fail it) and resolve
    // 'declined' so the transferring agent gets instant feedback.
    if (transferCallRef.current) {
      const pendingOffer = incomingTransferOffer;
      try { transferCallRef.current.reject(); } catch { /* already gone */ }
      transferCallRef.current = null;
      setIncomingTransferOffer(null);
      if (pendingOffer) {
        api.post({
          url: getApiUrl('/communication/transfer/resolve'),
          body: {
            action: 'declined', callSid: pendingOffer.callSid,
            conferenceSid: pendingOffer.conferenceSid, participantCallSid: pendingOffer.participantCallSid,
          },
          auth: true,
        }).catch(() => {});
      }
    }

    if (!device || !isDeviceReady) {
      toast({
        title: "Device Not Ready",
        description: "Please wait for the calling device to initialize",
        variant: "destructive",
      });
      return null;
    }

    // CRITICAL: If a multi-line or power-dialer session is active but this is a manual/non-dialer call,
    // auto-cleanup and clear mode flags to prevent the dialer from creating new batches.
    // NOTE: this PAUSES the dialer session (doesn't end it). The session stays
    // intact so it can be resumed after the manual call completes — either
    // via auto-resume on call-end or manually via the Resume button.
    //
    // EXCEPTION: conference joins for incoming-call flows (Hold & Answer,
    // Merge, resumeHeldCall) are NOT manual calls — they're part of the
    // call-waiting flow that the dialer session needs to ride out. If we
    // clear the mode flags here, auto-advance after the resumed call ends
    // fails its `isInPowerDialerModeRef` gate and the dialer redials the
    // same lead instead of advancing (Mark's "hold & answer → talk >1min
    // → end → redials Caius" bug). Detect by metadata.isConference.
    const isMultiLineDialer = params.metadata?.isMultiLine === true;
    const isPowerDialer = params.metadata?.source === 'power_dialer';
    // DialIQ's `predictive:` join is part of the power-dialer session it runs in, not a manual
    // call that should pause it.
    const isConferenceJoinForIncoming = params.metadata?.isConference === 'true'
      || (typeof params.to === 'string' && /^(conference|predictive):/.test(params.to));
    if (!isMultiLineDialer && !isPowerDialer && !isConferenceJoinForIncoming
        && (isInMultiLineModeRef.current || isInPowerDialerModeRef.current)) {
      console.warn('[TwilioDevice] Manual call initiated while dialer session active — pausing session');
      requestManualCallPause();
      setIsInMultiLineMode(false);
      setIsInPowerDialerMode(false);
    }

    // CALL-WAITING / GENERIC HANDOFF: if an activeCall still exists on the SDK
    // (e.g. agent pressed "Hold & Answer" on an incoming call — the old call
    // was placed on hold backend-side but the SDK leg is still alive), release
    // it before connecting to the new conference. Without this, Twilio throws
    // "A Call is already active" and the new connect() fails → the user sees
    // "Call Failed" right after the hold toast.
    //
    // Safety: backend outbound conferences set endConferenceOnExit=false on
    // the agent participant, so disconnecting the SDK leg does NOT collapse
    // the conference — the held customer stays in the conf with hold music.
    if (activeCallRef.current) {
      const oldCall = activeCallRef.current;
      // Wait for the 'disconnect' event to actually fire before moving on.
      // Twilio SDK v2 disconnect() schedules teardown asynchronously — the
      // Call stays 'open' for a moment even after disconnect() is invoked.
      // If device.connect() runs during that window the SDK throws
      // "A Call is already active" and the new call fails.
      const waitForClose = new Promise<void>((resolve) => {
        let settled = false;
        const finish = () => {
          if (settled) return;
          settled = true;
          try { oldCall.removeListener?.('disconnect', finish); } catch { /* ignore */ }
          try { oldCall.removeListener?.('cancel', finish); } catch { /* ignore */ }
          try { oldCall.removeListener?.('error', finish); } catch { /* ignore */ }
          resolve();
        };
        try {
          oldCall.on?.('disconnect', finish);
          oldCall.on?.('cancel', finish);
          oldCall.on?.('error', finish);
        } catch { /* ignore */ }
        // Trigger the teardown.
        try {
          oldCall.disconnect();
        } catch (err) {
          console.warn('[TwilioDevice] activeCall.disconnect() threw:', err);
          finish();
        }
        // Hard timeout — 1200ms gives the SDK enough time even on slow
        // networks. Was 600ms which wasn't enough for the "A Call is
        // already active" race window on multi-line handoffs.
        setTimeout(finish, 1200);
      });
      await waitForClose;

      // BELT-AND-SUSPENDERS: The Device singleton keeps its own internal
      // reference to active calls. Even after the Call event fires, Device
      // may still refuse a new connect() for a brief moment. Explicitly ask
      // the Device to disconnect ALL and poll until it reports no active calls.
      if (device) {
        try {
          const dev = device as any;
          if (typeof dev.disconnectAll === 'function') {
            dev.disconnectAll();
          }
          // Poll device.calls (or device.activeConnections) until empty, up to 1500ms.
          const maxWait = 1500;
          const pollInterval = 100;
          const startTime = Date.now();
          while (Date.now() - startTime < maxWait) {
            const activeCalls = dev.calls ?? dev.activeConnections?.() ?? [];
            const activeCount = Array.isArray(activeCalls) ? activeCalls.length
              : activeCalls instanceof Map ? activeCalls.size
              : 0;
            if (activeCount === 0) break;
            await new Promise(r => setTimeout(r, pollInterval));
          }
        } catch (err) {
          console.warn('[TwilioDevice] device cleanup threw:', err);
        }
      }
      activeCallRef.current = null;
    }

    // Request microphone permission before making the call
    const hasPermission = await requestMicrophonePermission();
    if (!hasPermission) {
      return null;
    }

    try {
      setCallStatus("connecting");
      // Set forceShowWidget flag if requested (for single-lead calls from dialer that should show widget)
      setForceShowWidget(params.forceShowWidget ?? false);

      // Create call parameters
      // Check if this is a conference join request (starts with "conference:")
      // DialIQ joins its conference with "predictive:" — it was being dialed as +1<digits>.
      let formattedTo: string;
      const isConferenceJoin = /^(conference|predictive):/.test(params.to);
      const isMultilineJoin = params.to.startsWith('multiline:');
      const isMultiLineDialer = params.metadata?.isMultiLine === true;

      // Start ringback audio for outbound phone calls
      // For regular calls: play ringback since we're calling someone
      // For multi-line dialer conference joins: play ringback since customers are being dialed
      // For regular conference joins (incoming call answer): don't play - customer is already on line
      if (!isConferenceJoin && !isMultilineJoin) {
        playRingback();
      } else if (isMultiLineDialer) {
        // Multi-line dialer joins a conference but customers are actively being called
        // So we should play ringback until one of them answers
        playRingback();
      }

      if (isConferenceJoin) {
        // For conference joins, pass the full string as-is
        // The backend TwiML app will handle this specially
        formattedTo = params.to;
      } else {
        // For regular phone calls, ensure phone number is in E.164 format (+1XXXXXXXXXX for US numbers)
        if (params.to.startsWith('+')) {
          formattedTo = params.to;
        } else {
          // Strip non-digits first
          const digits = params.to.replace(/\D/g, '');
          // If 11 digits starting with 1, it's a US number with country code - just add +
          // If 10 digits, add +1 prefix
          if (digits.length === 11 && digits.startsWith('1')) {
            formattedTo = `+${digits}`;
          } else if (digits.length === 10) {
            formattedTo = `+1${digits}`;
          } else {
            // Fallback - assume it needs +1 prefix (may produce invalid numbers for edge cases)
            formattedTo = `+1${digits}`;
          }
        }
      }

      const callParams: Record<string, string> = {
        To: formattedTo,
      };

      // Add from parameter if provided (caller ID selection)
      // Use custom parameter name to avoid collision with Twilio's standard From field
      if (params.from) {
        callParams.SelectedCallerId = params.from;
      }

      if (params.leadId) {
        callParams.LeadId = params.leadId;
      }

      if (params.metadata) {
        // Add leadName to top level for ActiveCallWidget
        if (params.metadata.lead_name) {
          callParams.leadName = String(params.metadata.lead_name);
        }
        // Store full metadata object for reference
        Object.entries(params.metadata).forEach(([key, value]) => {
          callParams[key] = String(value);
        });
      }

      // Connect with explicit audio constraints to ensure microphone is properly acquired
      // This is CRITICAL for two-way audio - without proper audio constraints,
      // the agent's voice may not be transmitted to the customer
      const call = await device.connect({
        params: callParams,
        rtcConstraints: {
          audio: {
            echoCancellation: true,
            noiseSuppression: true,
            autoGainControl: true,
            // Ensure we're not using a specific device that might not exist
            // Let browser choose default input device
          }
        }
      });

      // CRITICAL: Ensure call starts unmuted
      // Some edge cases can cause the call to start muted
      if (call.isMuted()) {
        console.warn('[TwilioDevice] Call started muted, unmuting...');
        call.mute(false);
      }

      setActiveCall(call);

      // Track last dialed number for redial (only outbound phone calls, not conference joins)
      if (!isConferenceJoin && !isMultilineJoin) {
        const rawDigits = formattedTo.replace(/^\+1/, '');
        const leadName = params.metadata?.lead_name || params.metadata?.leadName || '';
        setLastDialed(rawDigits, leadName);
      }

      // Store call metadata separately since Twilio doesn't preserve custom params after connection
      // For conference calls (incoming), use the metadata phoneNumber; for outbound calls, use formattedTo
      // DialIQ's `predictive:` join too, or its hangup toast reads "predictive:…" instead of a lead.
      const isConferenceCall = /^(conference|predictive):/.test(params.to);
      setCallMetadata({
        leadName: params.metadata?.lead_name || params.metadata?.leadName,
        leadId: params.leadId,
        phoneNumber: isConferenceCall ? (params.metadata?.phoneNumber || params.metadata?.phone_number || '') : formattedTo,
        leadEmail: params.metadata?.lead_email,
        source: params.metadata?.source,
        // Store parentCallSid for incoming calls - this is needed to match customer_disconnected events
        parentCallSid: params.metadata?.parentCallSid,
        outboundCallerId: params.from || params.displayFrom,
      });

      // CRITICAL: Set parentCallSidRef immediately for incoming calls
      // This ensures customer_disconnected events can match even before React re-renders
      if (params.metadata?.parentCallSid) {
        parentCallSidRef.current = params.metadata.parentCallSid;
      }

      // CRITICAL: Set the active conference name for 3-way calling support
      // For incoming calls (conference joins), extract conference name from "conference:{name}"
      // For outbound calls, use "call-{callSid}" format (set after call connects)
      if (isConferenceCall) {
        const conferenceName = params.to.replace(/^(conference|predictive):/, '');
        setCurrentConferenceName(conferenceName);
      }

      // Track if we're in multi-line mode to protect against incoming call conflicts
      // CRITICAL: Update BOTH ref (immediate) and state (async) to prevent incoming calls
      // from slipping through during the 1-2 render cycles between setState and useEffect
      if (isMultiLineDialer) {
        isInMultiLineModeRef.current = true;
        setIsInMultiLineMode(true);
      }

      // Determine call mode for session persistence
      // Multi-line dialer handles its own persistence, so we skip it here
      // Incoming calls (conference joins) and outbound calls are persisted for recovery
      const callMode = isConferenceCall
        ? (params.metadata?.parentCallSid ? 'incoming' : 'manual')
        : (params.metadata?.source === 'power_dialer' ? 'power_dialer' : 'manual');

      // Track call source so widget can show manual call UI over paused dialer session
      setActiveCallSource(isMultiLineDialer ? 'multiline' : isPowerDialer ? 'power_dialer' : 'manual');

      // Only persist non-multi-line calls (multi-line has its own persistence mechanism)
      const shouldPersist = !isMultiLineDialer && !params.metadata?.isRecovery;

      setupCallListeners(call, shouldPersist ? {
        leadId: params.leadId,
        leadName: params.metadata?.lead_name || params.metadata?.leadName,
        phoneNumber: isConferenceCall ? (params.metadata?.phoneNumber || params.metadata?.phone_number || '') : formattedTo,
        source: params.metadata?.source,
        mode: callMode,
        isRecovery: params.metadata?.isRecovery,
      } : undefined);

      return call;
    } catch (error: any) {

      setCallStatus("");
      toast({
        title: "Call Failed",
        description: error.message || "Failed to initiate call",
        variant: "destructive",
      });
      return null;
    }
  };

  // Disconnect call - ends the entire conference including all participants
  // When the agent disconnects from a conference, Twilio automatically ends the conference
  // and disconnects all participants. Individual participants can be removed using removeParticipantFromCall.
  /**
   * Disconnect the active call.
   *
   * Options:
   *   - `force`: bypass the call-waiting transition guard (used by the
   *     End Call button so user clicks always win over the safety guard).
   *   - `userInitiated`: mark this hangup as agent-initiated for toast
   *     attribution. Defaults to false. The End Call button + Skip / Pause /
   *     Stop session paths set this true. Auto-cleanup paths (customer hung
   *     up → backend broadcast → we tidy up the SDK; confirmed-voicemail
   *     auto-skip; multi-line batch transitions) MUST leave it false, or
   *     the disconnect toast will lie and tell the agent "you ended the
   *     call" when the customer was actually the one who hung up.
   */
  // LIVE TRANSFER (owner side): accept the stashed leg → we're live in the
  // conference with the customer; the backend relays 'accepted' so the
  // original agent's device drops. Decline rejects the leg (conference and
  // customer untouched — agent keeps talking).
  const acceptTransferOffer = useCallback(async (): Promise<boolean> => {
    const call = transferCallRef.current;
    const offer = incomingTransferOffer;
    if (!call || !offer) return false;
    try {
      const ok = await requestMicrophonePermission();
      if (!ok) return false;
      transferCallRef.current = null;
      setIncomingTransferOffer(null);
      setActiveCall(call);
      const acceptedSid = (call.parameters as any)?.CallSid as string | undefined;
      if (acceptedSid) setActiveCallSid(acceptedSid);
      (call as any).__transferAccepted = true; // accept-handler: treat as live (badge fix)
      setupCallListeners(call);
      call.accept();
      // 'in-progress' = the widget's isConnected value ("Ringing…" badge was
      // stuck because 'open' isn't a recognized connected status, 2026-07-23).
      setCallStatus('in-progress');
      // DIRECT metadata ("Unknown Caller" fix 2026-07-23): updateCallMetadata
      // requires a matching parentCallSid AND pre-existing metadata — both
      // absent on a transfer accept, so it silently no-opped and the active
      // call view showed "Unknown Caller". Set the standard shape directly.
      setCallMetadata({
        leadName: offer.leadName || undefined,
        leadId: offer.leadId || undefined,
        phoneNumber: offer.customerNumber || '',
        source: 'live_transfer',
        // ORIGINAL call sid — lets the owner TRANSFER BACK (the history row +
        // conference are keyed off this, not the owner's own client leg).
        parentCallSid: offer.callSid,
        // Conference sid — lets the holder's End terminate the WHOLE call.
        transferConferenceSid: offer.conferenceSid,
      } as any);
      api.post({
        url: getApiUrl('/communication/transfer/resolve'),
        body: {
          action: 'accepted', callSid: offer.callSid, targetName: undefined,
          // Call-forwarding (2026-07-23): CRM accept won the race — stop the
          // cell from ringing (sibling leg torn down server-side).
          conferenceSid: offer.conferenceSid,
          cancelLegSid: offer.phoneParticipantCallSid,
          // Who must drop = the offer's initiator (label-validated server-side).
          dropUserId: offer.dropUserId,
        },
        auth: true,
      }).catch(() => {});
      return true;
    } catch (e) {
      console.error('[Transfer] accept failed:', e);
      try { call.reject(); } catch {}
      transferCallRef.current = null;
      setIncomingTransferOffer(null);
      return false;
    }
  }, [incomingTransferOffer]);

  const declineTransferOffer = useCallback(async (): Promise<void> => {
    const call = transferCallRef.current;
    const offer = incomingTransferOffer;
    transferCallRef.current = null;
    setIncomingTransferOffer(null);
    try { call?.reject(); } catch {}
    if (offer) {
      api.post({
        url: getApiUrl('/communication/transfer/resolve'),
        body: {
          action: 'declined', callSid: offer.callSid,
          conferenceSid: offer.conferenceSid, participantCallSid: offer.participantCallSid,
          phoneParticipantCallSid: offer.phoneParticipantCallSid,
          isBack: offer.isBack,
        },
        auth: true,
      }).catch(() => {});
    }
  }, [incomingTransferOffer]);

  // (ref refreshed each render below — see disconnectFnRef)
  // Lock-screen call card (iOS CallKit): End/Mute pressed on the SYSTEM UI
  // must drive the real Twilio call — MobilePushBridge forwards the native
  // actions as window events (it can't reach this context directly).
  useEffect(() => {
    const onCkEnd = () => {
      try {
        activeCallRef.current?.disconnect();
      } catch { /* already gone */ }
    };
    const onCkMute = (e: Event) => {
      const muted = (e as CustomEvent).detail?.muted;
      try {
        activeCallRef.current?.mute(!!muted);
      } catch { /* no live call */ }
    };
    window.addEventListener("callkit:end", onCkEnd);
    window.addEventListener("callkit:mute", onCkMute);
    return () => {
      window.removeEventListener("callkit:end", onCkEnd);
      window.removeEventListener("callkit:mute", onCkMute);
    };
  }, []);

  const disconnect = (options?: { force?: boolean; userInitiated?: boolean; skipServerCancel?: boolean }) => {
    if (activeCall) {
      const callSid = activeCall.parameters.CallSid;

      // CALL-WAITING SAFETY: during a Hold & Answer / Merge handoff, spurious
      // auto-pause or auto-resume effects can fire disconnect() WHILE the new
      // incoming call is still stabilizing. Log evidence: agent SDK joins new
      // `incoming-*` conference at T, participant-leave fires at T+307ms,
      // `/cancel` POST fires at T+600ms — all while Caius is supposed to be
      // on hold. Kills the brand-new incoming leg + triggers the "resuming
      // call with caius / glitches ends call with incoming" cascade.
      //
      // The `__callwaiting_active` sessionStorage flag is set at the start
      // of answerCall and cleared only after the held call has fully resumed
      // AND ended. While set, block auto-fired disconnect() calls. User-
      // initiated hangups (ActiveCallWidget hangup button) pass force=true
      // to bypass the guard.
      if (!options?.force) {
        let inCallWaitingTransition = false;
        try { inCallWaitingTransition = sessionStorage.getItem('__callwaiting_active') === '1'; } catch {}
        if (inCallWaitingTransition) {
          console.warn('[TwilioDevice] disconnect() blocked — call-waiting transition in progress', { callSid });
          return;
        }
      }

      // Snapshot the conference name BEFORE disconnecting. Once the SDK
      // disconnects, the agent leg disappears from the conference and
      // participant-scan lookup on the backend would return nothing —
      // the customer's leg would keep ringing. Passing the conferenceName
      // lets backend go straight to the right conference by name.
      const conferenceNameSnapshot = currentConferenceName;

      // Mark this hangup as user-initiated ONLY when the caller explicitly says so.
      //
      // PREVIOUSLY this was set unconditionally on every disconnect() call —
      // automated cleanup paths (customer hung up → backend broadcast →
      // frontend tidies up the SDK; AMD confirmed voicemail → auto-advance;
      // batch transitions; etc.) falsely tagged hangups as agent-initiated.
      // The call.on('disconnect') toast then said "You ended the call with X"
      // when the customer was actually the one who hung up — Alexander Zaleczny's
      // reported bug, leads denied hanging up because they didn't.
      //
      // Now: only user-driven paths set this. The End Call button passes both
      // force + userInitiated; Skip / Pause / Stop pass userInitiated only.
      // Auto-cleanup paths pass nothing.
      if (options?.userInitiated) {
        hangupAttributionRef.current = 'user';
      }

      // Disconnect local browser call IMMEDIATELY - don't wait for API
      // This ensures responsive UI, especially on iPad/mobile where network can be slow
      activeCall.disconnect();

      // Fire-and-forget: Cancel the call on Twilio's servers
      // This ensures the client's phone stops ringing if call was still connecting
      // We do this AFTER local disconnect to prioritize UI responsiveness
      //
      // Gated by shouldCancelCall() to prevent the multi-path cascade we
      // saw in Hold & Answer flows (6 cancel POSTs per callSid in <1s).
      // WHOLE-CALL END for transferred calls (2026-07-23): the holder's End
      // must terminate the CONFERENCE — their client leg leaving alone would
      // strand the customer in silence. Fire the transfer 'end' action with
      // the ORIGINAL call sid + conference sid stored at accept.
      const meta: any = callMetadata;
      if (!options?.skipServerCancel && meta?.source === 'live_transfer' && meta?.parentCallSid) {
        api.post({
          url: getApiUrl('/communication/transfer/resolve'),
          body: { action: 'end', callSid: meta.parentCallSid, conferenceSid: meta.transferConferenceSid },
          auth: true,
        }).catch(() => { /* conference may already be over */ });
      }

      // LIVE TRANSFER (2026-07-23): when the agent drops AFTER the owner
      // accepted a transfer, the customer + owner must keep talking — the
      // server-side cancel would kill the customer leg. Agent legs join with
      // endConferenceOnExit=false, so a device-only disconnect is safe.
      if (callSid && !options?.skipServerCancel && meta?.source !== 'live_transfer' && shouldCancelCall(callSid)) {
        // Fire-and-forget, retried through a database blip (sendHangupCancel). Other
        // failures are still ignored: the local disconnect already succeeded.
        void sendHangupCancel(
          () => api.post({
            url: getApiUrl(`/communication/calls/${callSid}/cancel`),
            body: {
              reason: 'agent_hangup',
              // Hint to backend: look up this conference directly. Critical
              // for post-resume scenarios where the agent's CURRENT SDK sid
              // doesn't match the original conference name.
              conferenceName: conferenceNameSnapshot,
            },
            auth: true,
          }),
          {
            stillThisCall: () => {
              const current = getCallSid(activeCallRef.current as any);
              return !current || current === callSid;
            },
            // Every try failed: let the agent's next press through instead of the 30 s dedup.
            onGiveUp: () => clearCancelDedup(callSid),
          },
        );
      }
    }
  };

  disconnectFnRef.current = disconnect; // refresh every render (stale-closure fix)

  // Mirror the in-app mute onto the iOS lock-screen call card (no-op off
  // native). Fire-and-forget — cosmetic sync must never touch call flow.
  const mirrorMuteToCallKit = (muted: boolean, call: Call | null) => {
    import("@/lib/capacitor/callkit")
      .then((ck) => ck.setCallKitMuted(muted, (call as any)?.__ckUuid || undefined))
      .catch(() => {});
  };

  // Mute call
  const mute = () => {
    if (activeCall) {
      activeCall.mute(true);
      setIsMuted(true);
      mirrorMuteToCallKit(true, activeCall);
    }
  };

  // Unmute call
  const unmute = () => {
    if (activeCall) {
      activeCall.mute(false);
      setIsMuted(false);
      mirrorMuteToCallKit(false, activeCall);
    }
  };

  // Hold call (put customer on hold)
  // Optional callSid parameter allows specifying which call to hold (e.g., customer's callSid in multi-line mode)
  const hold = (callSid?: string) => {
    const targetCallSid = callSid || activeCall?.parameters.CallSid;
    if (targetCallSid && device) {
      // Send hold request to backend to play music for customer
      api.post({
        url: getApiUrl(`/communication/calls/${targetCallSid}/hold`),
        body: { onHold: true },
        auth: true,
      }).then(() => {
        setIsOnHold(true);
        toast({
          title: "Call On Hold",
          description: "Lead is on hold with music",
        });
      }).catch((error) => {
        toast({
          title: "Hold Failed",
          description: "Failed to put call on hold",
          variant: "destructive",
        });
      });
    }
  };

  // Unhold call (resume call)
  // Optional callSid parameter allows specifying which call to unhold (e.g., customer's callSid in multi-line mode)
  const unhold = (callSid?: string) => {
    const targetCallSid = callSid || activeCall?.parameters.CallSid;
    if (targetCallSid && device) {
      // Send unhold request to backend
      api.post({
        url: getApiUrl(`/communication/calls/${targetCallSid}/hold`),
        body: { onHold: false },
        auth: true,
      }).then(() => {
        setIsOnHold(false);
        toast({
          title: "Call Resumed",
          description: "Call has been resumed",
        });
      }).catch((error) => {
        toast({
          title: "Resume Failed",
          description: "Failed to resume call",
          variant: "destructive",
        });
      });
    }
  };

  // Start recording call
  const startRecording = () => {
    if (activeCall && device) {
      // Send recording request to backend (without /api prefix)
      api.post({
        url: getApiUrl(`/communication/calls/${activeCall.parameters.CallSid}/recording`),
        body: { recording: true },
        auth: true,
      }).then(() => {
        setIsRecording(true);
        toast({
          title: "Recording Started",
          description: "Call is now being recorded",
        });
      }).catch((error) => {
        toast({
          title: "Recording Failed",
          description: "Failed to start call recording",
          variant: "destructive",
        });
      });
    }
  };

  // Stop recording call
  const stopRecording = () => {
    if (activeCall && device) {
      // Send stop recording request to backend (without /api prefix)
      api.post({
        url: getApiUrl(`/communication/calls/${activeCall.parameters.CallSid}/recording`),
        body: { recording: false },
        auth: true,
      }).then(() => {
        setIsRecording(false);
        toast({
          title: "Recording Stopped",
          description: "Call recording has been stopped",
        });
      }).catch((error) => {
        toast({
          title: "Stop Recording Failed",
          description: "Failed to stop call recording",
          variant: "destructive",
        });
      });
    }
  };

  // Send DTMF digits (tones) during an active call
  const sendDigits = (digits: string): boolean => {
    if (!activeCall) {

      return false;
    }

    try {
      // Use Twilio SDK's native sendDigits method to send DTMF tones
      // This sends the tones directly through the WebRTC audio channel
      activeCall.sendDigits(digits);

      return true;
    } catch (error) {

      return false;
    }
  };

  // Create Bridged Call (phone-based calling)
  const createBridgedCall = async (params: { to: string; from?: string; leadId?: string; leadName?: string }) => {
    try {
      const result = await communicationService.createBridgeCall(params);

      // Add to bridged calls state
      setBridgedCalls(prev => {
        const updated = new Map(prev);
        updated.set(result.callSid, {
          callSid: result.callSid,
          status: result.status,
          leadPhone: params.to,
          leadName: params.leadName,
          createdAt: new Date(),
        });
        return updated;
      });

      // Show ActiveCallWidget for this bridged call
      setActiveCallSid(result.callSid);
      setCallStatus('ringing');

      return result;
    } catch (error) {
      throw error;
    }
  };

  // Fetch call forwarding preference on mount
  useEffect(() => {
    const fetchCallForwardingPreference = async () => {
      try {
        const response = await api.get({
          url: getApiUrl('/dialer-preferences'),
          auth: true,
        });

        // API returns { success, statusCode, data: preferences }
        const responseBody = response.data as any;
        if (responseBody?.data) {
          const data = responseBody.data;
          const forwardingEnabled = data.forwardCallsToPhone ?? false;
          setForwardCallsToPhone(forwardingEnabled);
        }
      } catch (error) {

        // Default to false (browser calling)
      }
    };

    fetchCallForwardingPreference();
  }, []);

  // Track subscription status for debugging
  const supabaseSubscribedRef = useRef<boolean>(false);

  // ============================================================================
  // SUPABASE EVENT HANDLERS
  // These are called by IncomingCallContext which manages the Supabase subscription
  // to avoid duplicate subscriptions causing the channel to close
  // ============================================================================

  // Handle customer_disconnected event - disconnect agent when customer hangs up
  const handleCustomerDisconnected = useCallback((payload: { parentCallSid: string; customerCallSid?: string; callStatus?: string }) => {
    const { parentCallSid, callStatus } = payload;

    // CRITICAL: Skip voicemail events — let the PowerDialerSessionLogic handler manage these.
    // AMD voicemail detection should NOT disconnect the call (false positive protection).
    // The call stays alive so the agent can keep talking if AMD was wrong.
    // PowerDialerSessionLogic marks the result as "voicemail" for retry logic.
    if (callStatus === 'voicemail') {
      return;
    }

    // CRITICAL: Skip disconnect if we're mid call-waiting transition (Hold &
    // Answer / Merge handoff). Backend broadcasts customer_disconnected for
    // Caius's original leg when he gets redirected to hold music — but by
    // the time the broadcast arrives, activeCallRef has ALREADY been
    // transitioned to the new incoming-call SDK leg. Firing disconnect here
    // would kill the NEW incoming call, not the old Caius leg. Symptom:
    // "Hold & Answer never shows incoming caller UI, drops everything."
    // The sessionStorage `__callwaiting_active` flag is set at the start of
    // answerCall() and cleared after the call-waiting cycle fully ends.
    let inCallWaitingTransition = false;
    try { inCallWaitingTransition = sessionStorage.getItem('__callwaiting_active') === '1'; } catch {}
    if (inCallWaitingTransition) {
      return;
    }

    // Check if we have an active call that matches
    const isOutboundMatch = activeCallSidRef.current && activeCallSidRef.current === parentCallSid;
    const isIncomingMatch = parentCallSidRef.current && parentCallSidRef.current === parentCallSid;

    if (isOutboundMatch || isIncomingMatch) {
      // NOTE: The "lead has hung up" toast is now fired by the call.on("disconnect")
      // handler with full lead-name attribution + duration + follow-up nudge.
      // Disconnecting the SDK call below triggers that handler.

      // Disconnect the Twilio browser call
      if (activeCallRef.current) {
        try {
          activeCallRef.current.disconnect();
        } catch (disconnectError) {
          console.error('[TwilioDevice] Error disconnecting browser call:', disconnectError);
        }
      }

      releaseWakeLock();
      stopRingback();
      stopAudioLevelMonitoring();
      clearPersistedCallSession();

      // Clear call state
      setActiveCall(null);
      setActiveCallSid(null);
      setCallMetadata(null);
      setCallStatus('');
      setIsMuted(false);
      setIsOnHold(false);
      setIsRecording(false);
      setCallDuration(0);
      setForceShowWidget(false);
      setActiveCallSource(null);
      setCurrentConferenceName(null);
      setConferenceParticipants([]);

      setBridgedCalls(prev => {
        const updated = new Map(prev);
        updated.delete(parentCallSid);
        return updated;
      });

      parentCallSidRef.current = null;
      activeCallSidRef.current = null;

      if (durationIntervalRef.current) {
        clearInterval(durationIntervalRef.current);
        durationIntervalRef.current = null;
      }

      cleanupSpeakerAudioPipeline();
      if (remoteAudioRef.current) {
        remoteAudioRef.current.srcObject = null;
      }
    }
  }, [toast, releaseWakeLock, stopRingback, stopAudioLevelMonitoring, cleanupSpeakerAudioPipeline, clearPersistedCallSession]);

  // Handle participant_connected event - update participant status when they answer
  const handleParticipantConnected = useCallback((payload: { participantSid: string; conferenceSid?: string; conferenceName?: string }) => {
    const { participantSid } = payload;

    // TRANSFER FAST-PATH (2026-07-23, "took forever to drop"): the conference
    // participant-join webhook lands ~immediately when the owner answers —
    // faster than owner-FE → resolve → realtime broadcast. Panels match this
    // against their pendingTransfer.participantCallSid and treat it as
    // accepted instantly (idempotent with the real broadcast that follows).
    try {
      window.dispatchEvent(new CustomEvent('transfer:participant-joined', { detail: { participantSid } }));
    } catch { /* best effort */ }

    setConferenceParticipants(prev => {
      const updated = prev.map(p =>
        p.participantSid === participantSid
          ? { ...p, status: 'connected' as const }
          : p
      );
      return updated;
    });

    stopRingback();
  }, [stopRingback]);

  // Handle participant_left event - remove participant from UI when they disconnect
  const handleParticipantLeft = useCallback((payload: { participantSid: string; conferenceSid?: string; conferenceName?: string }) => {
    const { participantSid } = payload;

    setConferenceParticipants(prev => prev.filter(p => p.participantSid !== participantSid));
  }, []);

  // Handle call_status_updated event - update bridge call status in real-time
  const handleCallStatusUpdated = useCallback((payload: { callSid: string; status: string; duration?: number; isBridgedCall?: boolean }) => {
    const { callSid, status, duration, isBridgedCall } = payload;

    if (isBridgedCall) {
      setBridgedCalls(prev => {
        const call = prev.get(callSid);
        if (call) {
          const updated = new Map(prev);
          updated.set(callSid, { ...call, status });
          return updated;
        }
        return prev;
      });

      setCallStatus(status);

      if (status === 'completed' || status === 'failed' || status === 'no-answer' || status === 'canceled') {
        setTimeout(() => {
          setBridgedCalls(prev => {
            const updated = new Map(prev);
            updated.delete(callSid);
            return updated;
          });
          setActiveCallSid(null);
          setCallStatus('');
          setCallDuration(duration || 0);
        }, 2000);
      }
    }
  }, []);

  // Handle phone_answered event - agent picked up on personal phone during simultaneous ring
  const handlePhoneAnswered = useCallback((payload: { callSid: string; phoneCallSid?: string; conferenceName?: string; status?: string }) => {
    const { callSid, phoneCallSid, conferenceName, status } = payload;

    setBridgedCalls(prev => {
      const updated = new Map(prev);
      updated.set(callSid, {
        callSid,
        status: status || 'in-progress',
        leadPhone: '',
        leadName: 'Forwarded Call',
        createdAt: new Date(),
        isBridgedCall: true,
        phoneCallSid,
        conferenceName,
      });
      return updated;
    });

    setActiveCallSid(callSid);
    setCallStatus(status || 'in-progress');
    parentCallSidRef.current = callSid;

    if (durationIntervalRef.current) {
      clearInterval(durationIntervalRef.current);
    }
    setCallDuration(0);
    durationIntervalRef.current = setInterval(() => {
      setCallDuration(prev => prev + 1);
    }, 1000);

    toast({
      title: 'Call Connected',
      description: 'Incoming call answered on your phone. Widget shows call status.',
    });
  }, [toast]);

  // Handle customer_answered event - customer picked up, transition from
  // "ringing" to "in-progress". For multi-line dialer the winning lead can
  // differ from the lead that was attached to the agent's outbound call
  // (agent dials a placeholder conference; the backend picks whichever
  // customer answers first). We overwrite callMetadata with the winner's
  // lead details so the MINIMIZED dialer widget shows the correct POC
  // — the expanded MultiLineDialerSession already handles this via
  // callSid matching. (Mark flagged: "minimized shows wrong POC".)
  const handleCustomerAnswered = useCallback((payload: {
    parentCallSid: string;
    callerId?: string;
    leadId?: string;
    leadName?: string;
    customerCallSid?: string;
    // Phone number the customer was dialed on; backend sends it so we can
    // populate the widget's phoneNumber field on the winning line.
    phoneNumber?: string;
    customerPhone?: string;
    isMultiLine?: boolean;
  }) => {
    // Only update if we have an active call matching the parentCallSid
    if (activeCallSidRef.current && activeCallSidRef.current === payload.parentCallSid) {
      setCallStatus("in-progress");

      setCallMetadata(prev => {
        if (!prev) return prev;
        const next = { ...prev };
        // Real outbound caller ID (the number the backend used to dial)
        if (payload.callerId) next.outboundCallerId = payload.callerId;
        // Winner lead — replace stale placeholder so minimized widget is right
        if (payload.leadId) next.leadId = payload.leadId;
        if (payload.leadName) next.leadName = payload.leadName;
        const winnerPhone = payload.customerPhone || payload.phoneNumber;
        if (winnerPhone) next.phoneNumber = winnerPhone;
        return next;
      });

      // PICKUP CHIME: audio cue for manual + power dialer when customer
      // answers. Helps the agent realize they should start talking NOW,
      // similar pattern to the multi-line pickup chime. For multi-line
      // (`isMultiLine: true`), the dialer has its own chime trigger that
      // fires earlier (on polling detection of "connecting" — ~800ms after
      // pickup, before AMD confirms). Skip here to avoid double-firing.
      if (!payload.isMultiLine && typeof window !== 'undefined') {
        try { window.dispatchEvent(new Event('dialer:pickup-chime')); } catch {}
      }
    }
  }, []);

  // Which demo call has already "been answered" — see the two effects below.
  const demoAnsweredSidRef = useRef<string | null>(null);

  // DEMO SANDBOX: relay the fake Device's local "customer picked up" signal.
  //
  // An outbound call sits on "ringing" until a `customer_answered` broadcast arrives from
  // the backend — correct, because the agent's leg connecting doesn't mean the lead
  // answered. But a manual or single-lead dial in the sandbox is resolved entirely by the
  // fake Device in the browser, so the backend has no leg to report on and that broadcast
  // never comes: the call rings forever. (Multi-line is unaffected — its verdict really
  // does come from the server, which is why only the manual path was stuck.)
  //
  // demo-voice-device dispatches this window event instead, and it is routed through the
  // SAME handler as the real broadcast so there is only one transition path to reason
  // about. No-ops entirely outside the demo build.
  useEffect(() => {
    if (!isDemoMode() || typeof window === 'undefined') return;

    const onDemoAnswered = (e: Event) => {
      const detail = (e as CustomEvent).detail;
      if (!detail?.parentCallSid) return;

      // Drive the status DIRECTLY, then hand off for metadata.
      //
      // handleCustomerAnswered only acts when activeCallSidRef.current already equals
      // parentCallSid. For a real call that ref is populated from the backend's CallSid
      // before the broadcast can arrive, so the guard is free. Here both values come from
      // the same browser within a few hundred ms, and any ordering hiccup — or another
      // code path re-asserting 'ringing' — silently swallows the transition and the call
      // rings forever, which is exactly the symptom this is meant to cure.
      //
      // Setting it here first makes the outcome independent of that race. The delegate
      // call still runs for caller-ID/lead metadata and the pickup chime; when the ref
      // does match it simply sets the same value twice, which is harmless.
      demoAnsweredSidRef.current = detail.parentCallSid;
      setCallStatus('in-progress');
      handleCustomerAnswered(detail);
    };

    window.addEventListener('insuracentral-demo-customer-answered', onDemoAnswered);
    return () => window.removeEventListener('insuracentral-demo-customer-answered', onDemoAnswered);
  }, [handleCustomerAnswered]);

  // DEMO SANDBOX: hold the answered state against writers that push it back to "ringing".
  //
  // Setting 'in-progress' once wasn't enough — the call connects and the badge still reads
  // "Ringing…". A manual dial also creates a BACKEND session, and the status pollers /
  // socket handlers that follow it (handleCallStatusUpdated, EnhancedDialer's
  // callStatusInterval) keep reporting 'ringing': the backend has a session but no real
  // Twilio leg to observe, because the whole call lives in the browser. They aren't wrong
  // so much as uninformed, and whichever of them fires last wins.
  //
  // Rather than teach every writer about the sandbox, re-assert the answer here. The
  // guard is scoped to the exact CallSid the fake device answered, and clears when the
  // call ends, so it can never pin a genuinely ringing call.
  useEffect(() => {
    if (!isDemoMode()) return;

    // Call over — stop holding.
    if (!callStatus || callStatus === 'closed' || callStatus === 'error') {
      demoAnsweredSidRef.current = null;
      return;
    }

    // Deliberately NOT gated on activeCallSidRef matching. Requiring that match is what
    // silently killed the first attempt at this fix: both SIDs are minted in the browser
    // within a few hundred ms, so the ref isn't reliably populated when this runs. In a
    // demo build there is only ever one call in flight, and the flag is cleared the
    // moment the call ends, so the looser check costs nothing and actually fires.
    if (demoAnsweredSidRef.current && callStatus === 'ringing') {
      setCallStatus('in-progress');
    }
  }, [callStatus]);

  // Partially update call metadata (e.g., when backend reports actual outbound caller ID)
  const updateCallMetadata = useCallback((updates: Partial<CallMetadata>) => {
    setCallMetadata(prev => prev ? { ...prev, ...updates } : null);
  }, []);

  // NOTE: All Supabase realtime event listeners for incoming_calls channel have been moved to IncomingCallContext
  // to avoid duplicate subscriptions which cause the channel to close.
  // IncomingCallContext handles: incoming_call, call_ended, customer_answered, customer_disconnected, etc.
  // This useEffect is kept but disabled - the channel is managed by IncomingCallContext
  useEffect(() => {
    if (!userId) return;

    // DISABLED: This was causing the channel to close because both TwilioDeviceContext
    // and IncomingCallContext were subscribing to the same channel name.
    // All event listeners are now in IncomingCallContext.

    // Keep this here as a placeholder in case we need separate events in the future
    const setupCustomerEventListeners = async () => {
      // NO-OP: Subscription handled by IncomingCallContext
    };

    setupCustomerEventListeners();

    return () => {
      // No cleanup needed - IncomingCallContext manages the channel
    };
  }, [userId]);

  // Initialize device on mount (Twilio services always on, mic requested only when needed)
  useEffect(() => {
    // Suppress Twilio console logs
    suppressTwilioLogs();

    unmountedRef.current = false; // StrictMode re-runs this on the same instance
    initializeDevice();

    return () => {
      // Cleanup on unmount — remove listeners before destroying
      if (device) {
        cleanupDevice(device);
      }
      unmountedRef.current = true;
      // `device` above is the mount-time value (null), so the device this provider actually built
      // was never shut down. Leave one with a live call alone, as before.
      const built = builtDeviceRef.current;
      if (built && built !== device && !activeCallRef.current && !built.calls?.length) cleanupDevice(built);
      if (durationIntervalRef.current) {
        clearInterval(durationIntervalRef.current);
      }
      if (tokenRefreshTimeoutRef.current) {
        clearTimeout(tokenRefreshTimeoutRef.current);
      }
      if (reconnectTimeoutRef.current) {
        clearTimeout(reconnectTimeoutRef.current);
      }
      if (carrierRecheckRef.current) {
        clearTimeout(carrierRecheckRef.current);
      }
      if (heartbeatIntervalRef.current) {
        clearInterval(heartbeatIntervalRef.current);
      }
      // Clean up audio monitoring and speaker audio
      if (audioLevelIntervalRef.current) {
        clearInterval(audioLevelIntervalRef.current);
      }
      if (micAudioContextRef.current) {
        micAudioContextRef.current.close().catch(() => {});
      }
      if (speakerAudioContextRef.current) {
        speakerAudioContextRef.current.close().catch(() => {});
      }

      // Restore console logs
      restoreConsoleLogs();
    };
  }, []); // Only run once on mount

  // ── RECOVER A FAILED DEVICE INIT ──────────────────────────────────────────
  //
  // The effect above is `[]` — one shot. `initializeDevice()` fetches the Twilio voice
  // token from /communication/voice/token, which is MFA-gated, so a single 403 during the
  // login window makes it throw "Failed to obtain access token" and the device is dead for
  // the life of the page. Observed on prod 2026-08-17: sign in, verify, pick an agency,
  // land on the dashboard — blank — and a manual refresh fixes it, because a refresh
  // remounts this provider against a session that is stamped by then.
  //
  // Two triggers, because the first attempt can fail for more than one reason:
  //   1. 'mfa-satisfied' — the axios layer fires this the moment the gate stops refusing.
  //   2. a short bounded retry — covers a cold start or a transient network failure, which
  //      would otherwise be just as permanent.
  //
  // Guarded on `device` so a healthy device is never re-initialised, and the interval is
  // cleared as soon as one exists.
  useEffect(() => {
    if (device) return; // already up — nothing to recover

    let attempts = 0;
    const MAX_ATTEMPTS = 5;
    const RETRY_MS = 4000;

    const retry = () => {
      if (deviceRef.current) return; // came up in the meantime
      if (carrierSwitchRef.current === 'building') return; // the carrier switch is building it
      if (!planHasPhoneRef.current) return; // plan has no phone — never a failure
      try {
        void initializeDevice();
      } catch {
        /* initializeDevice reports its own errors; never throw from a recovery path */
      }
    };

    const onMfaSatisfied = () => retry();
    window.addEventListener('mfa-satisfied', onMfaSatisfied);

    // Carrier switch: the old device is gone, build the new one now rather than on the first tick.
    if (carrierSwitchRef.current === 'rebuild') {
      carrierSwitchRef.current = 'building';
      void initializeDevice().finally(() => { carrierSwitchRef.current = null; });
    }

    const timer = setInterval(() => {
      attempts += 1;
      if (deviceRef.current || attempts > MAX_ATTEMPTS || !planHasPhoneRef.current) {
        clearInterval(timer);
        // Recovery is over. If the device never came up, stop swallowing errors:
        // close the window and tell the user once, so a genuinely dead dialer is
        // visible rather than silently absent. (Not on a plan without a phone.)
        if (!deviceRef.current && !startupRecoveryDoneRef.current && planHasPhoneRef.current) {
          startupRecoveryDoneRef.current = true;
          toast({
            title: "Calling unavailable",
            description: "Couldn't start the dialer. Reload the page to try again.",
            variant: "destructive",
          });
        }
        return;
      }
      retry();
    }, RETRY_MS);

    return () => {
      window.removeEventListener('mfa-satisfied', onMfaSatisfied);
      clearInterval(timer);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [device]);

  // Provider-neutral active-call helpers (Twilio impl). Delegate to the shared
  // duck-typed helpers so the resolved calls are byte-identical to the old
  // inline `.parameters?.CallSid` / `.on('disconnect', …)` / `.disconnect()`.
  const getActiveCallSid = useCallback(
    () => getCallSid(activeCallRef.current as any) ?? activeCallSidRef.current,
    []
  );
  const getCallParam = useCallback(
    (key: 'To' | 'From' | 'LeadId' | 'CallSid') => getCallParamHelper(activeCallRef.current as any, key),
    []
  );
  const onActiveCallDisconnect = useCallback(
    (cb: () => void) => onCallDisconnect(activeCallRef.current as any, cb),
    []
  );
  const hangupActiveCall = useCallback(() => hangupCall(activeCallRef.current as any), []);

  const value: TwilioDeviceContextValue = useMemo(() => ({
    device,
    activeCall,
    activeCallSid,
    callMetadata,
    isDeviceReady,
    isDeviceRegistering,
    deviceError,
    forceShowWidget,
    connect,
    disconnect,
    mute,
    unmute,
    hold,
    unhold,
    startRecording,
    stopRecording,
    sendDigits,
    isMuted,
    isOnHold,
    setIsOnHold,
    isRecording,
    callStatus,
    callDuration,
    remoteAudioRef,
    forwardCallsToPhone,
    setForwardCallsToPhone,
    initializeDevice,
    armCoachBargeAutoAccept,
    setCoachBargeExpectedSid,
    disarmCoachBargeAutoAccept,
    disconnectCoachBarge,
    createBridgedCall,
    bridgedCalls,
    microphoneAudioLevel,
    isHardwareMuted,
    startAudioMonitoring,
    stopAudioMonitoring,
    audioInputDevices,
    audioOutputDevices,
    selectedInputDeviceId,
    selectedOutputDeviceId,
    setAudioInputDevice,
    setAudioOutputDevice,
    refreshAudioDevices,
    microphoneGain,
    speakerVolume,
    setMicrophoneGain,
    setSpeakerVolume,
    conferenceParticipants,
    isAddingParticipant,
    addParticipantToCall,
    removeParticipantFromCall,
    removeParticipant: removeParticipantFromCall,
    muteParticipant,
    holdParticipant,
    refreshConferenceParticipants,
    setActiveConference,
    currentConferenceName,
    stopRingback,
    playRingback,
    isInMultiLineMode,
    setIsInMultiLineMode,
    isInPowerDialerMode,
    setIsInPowerDialerMode,
    handleCustomerDisconnected,
    handleParticipantConnected,
    handleParticipantLeft,
    handleCallStatusUpdated,
    handlePhoneAnswered,
    handleCustomerAnswered,
    updateCallMetadata,
    isRecoveringCall,
    recoveryError,
    isReconnecting,
    reconnectDevice,
    lastDialedNumber,
    lastDialedLeadName,
    activeCallSource,
    getActiveCallSid,
    getCallParam,
    onActiveCallDisconnect,
    hangupActiveCall,
    incomingTransferOffer,
    acceptTransferOffer,
    declineTransferOffer,
    wakeLockSupported: typeof navigator !== 'undefined' && 'wakeLock' in navigator,
  }), [
    device, activeCall, activeCallSid, callMetadata, activeCallSource,
    isDeviceReady, isDeviceRegistering, deviceError, forceShowWidget, lastDialedNumber, lastDialedLeadName,
    connect, disconnect, mute, unmute, hold, unhold, startRecording, stopRecording, sendDigits,
    isMuted, isOnHold, isRecording, callStatus, callDuration,
    forwardCallsToPhone, initializeDevice, createBridgedCall, bridgedCalls,
    microphoneAudioLevel, isHardwareMuted, startAudioMonitoring, stopAudioMonitoring,
    audioInputDevices, audioOutputDevices, selectedInputDeviceId, selectedOutputDeviceId,
    setAudioInputDevice, setAudioOutputDevice, refreshAudioDevices,
    microphoneGain, speakerVolume, setMicrophoneGain, setSpeakerVolume,
    conferenceParticipants, isAddingParticipant,
    addParticipantToCall, removeParticipantFromCall, muteParticipant, holdParticipant,
    refreshConferenceParticipants, setActiveConference, currentConferenceName, stopRingback, playRingback,
    isInMultiLineMode, isInPowerDialerMode,
    handleCustomerDisconnected, handleParticipantConnected, handleParticipantLeft,
    handleCallStatusUpdated, handlePhoneAnswered, handleCustomerAnswered, updateCallMetadata,
    isRecoveringCall, recoveryError, isReconnecting, reconnectDevice,
    getActiveCallSid, getCallParam, onActiveCallDisconnect, hangupActiveCall,
    incomingTransferOffer, acceptTransferOffer, declineTransferOffer,
  ]);

  return (
    <TwilioDeviceContext.Provider value={value}>
      {children}
      {/* Audio element for remote audio (customer voice)
          Using visibility:hidden instead of display:none to avoid browser autoplay restrictions
          playsInline prevents iOS from going fullscreen */}
      <audio
        ref={remoteAudioRef}
        autoPlay
        playsInline
        style={{ position: 'absolute', width: 1, height: 1, opacity: 0, pointerEvents: 'none' }}
      />
    </TwilioDeviceContext.Provider>
  );
};

export const useTwilioDeviceContext = () => {
  const context = useContext(TwilioDeviceContext);
  if (!context) {
    throw new Error("useTwilioDeviceContext must be used within TwilioDeviceProvider");
  }
  return context;
};
